<template>
  <div class="career-simulator-container">
    
    <!-- Upload Error Modal -->
    <transition name="fade">
      <div v-if="errorMessage" class="custom-error-modal-overlay" @click.self="errorMessage = ''">
        <div class="custom-error-modal">
          <div class="modal-icon">🚨</div>
          <h3 style="color: #991B1B; margin: 0 0 0.5rem 0; font-size: 1.4rem; font-weight: 800;">
            {{ $t('orgDashboard.userDashboard.careerSimulator.uploadBlocked') }}
          </h3>
          <p style="color: #4B5563; font-size: 1rem; margin: 0 0 1.5rem 0; line-height: 1.5;">{{ errorMessage }}</p>
          <button class="btn-primary" style="background: #EF4444; width: 100%; box-shadow: none;" @click="errorMessage = ''">
            {{ $t('orgDashboard.userDashboard.careerSimulator.gotIt') }}
          </button>
        </div>
      </div>
    </transition>

    <!-- History Node Inspect Modal -->
    <transition name="fade">
      <div v-if="selectedHistoryNode" class="custom-error-modal-overlay" style="z-index: 100001;" @click.self="selectedHistoryNode = null">
        <div class="history-modal">
          <div class="modal-header">
            <div>
              <span class="eyebrow">{{ selectedHistoryNode.dates }}</span>
              <h2>{{ selectedHistoryNode.title }}</h2>
              <p class="org-name">{{ selectedHistoryNode.company }}</p>
            </div>
            <button class="close-btn" @click="selectedHistoryNode = null">×</button>
          </div>
          <div class="modal-body mt-4">
            <p class="history-desc">{{ selectedHistoryNode.impact_narrative }}</p>
            <h6 class="mt-4 mb-2">{{ $t('orgDashboard.userDashboard.careerSimulator.keyAchievements') }}</h6>
            <ul class="project-list">
              <li v-for="(proj, i) in selectedHistoryNode.key_learnings" :key="i">{{ proj }}</li>
            </ul>
            <h6 class="mt-4 mb-2">{{ $t('orgDashboard.userDashboard.careerSimulator.appliedSkills') }}</h6>
            <div class="skill-chips has">
              <span v-for="skill in selectedHistoryNode.skills" :key="skill">{{ skill }}</span>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <!-- Full Ribbon Modal (Triggered by Sankey Dot) -->
     <transition name="fade">
      <div v-if="showHistoryModal && processedHistory.length > 0" 
           class="custom-error-modal-overlay ribbon-modal-overlay" 
           style="z-index: 99999;" 
           @click.self="showHistoryModal = false">
        
        <div class="ribbon-modal-card">
          <button class="close-btn absolute-close" @click="showHistoryModal = false">×</button>
          
          <div class="ribbon-header-area">
            <div>
              <h1 class="page-title">{{ $t('orgDashboard.userDashboard.careerSimulator.careerTrajectory') }}</h1>
              <p class="page-subtitle" style="text-transform: uppercase; font-size: 0.85rem; font-weight: 700; letter-spacing: 0.05em;">
                {{ $t('orgDashboard.userDashboard.careerSimulator.careerDurationFlow', { duration: totalCareerDuration }) }}
              </p>
            </div>
            <div class="ribbon-legend">
              <span class="legend-item"><div class="legend-dash solid"></div> {{ $t('orgDashboard.userDashboard.careerSimulator.experienceFlow') }}</span>
              <span class="legend-item"><div class="legend-dash dashed"></div> {{ $t('orgDashboard.userDashboard.careerSimulator.projectedNextMove') }}</span>
            </div>
          </div>

          <div class="ribbon-container mt-4" 
               ref="ribbonArea"
               :class="{ 'is-panning': isPanning }"
               @mousedown="startPan" 
               @mousemove="scrubRibbon" 
               @mouseup="endPan" 
               @mouseleave="endPan(); hoveredHistoryIndex = null;">
            
            <div class="ribbon-toolbar">
              <button @click="zoomIn" :title="$t('orgDashboard.userDashboard.careerSimulator.zoomIn')">➕</button>
              <button @click="zoomOut" :title="$t('orgDashboard.userDashboard.careerSimulator.zoomOut')">➖</button>
              <button @click="resetZoom" :title="$t('orgDashboard.userDashboard.careerSimulator.resetView')">⛶</button>
            </div>

            <!-- THE ZOOM/PAN LAYER -->
            <div class="ribbon-pan-zoom-layer" 
                 ref="zoomLayer" 
                 :style="{ transform: `translate(${panX}px, ${panY}px) scale(${zoomLevel})`, transition: isPanning ? 'none' : 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)', position: 'relative', width: svgCanvasWidth + 'px', height: '450px' }">
              
              <!-- 1. THE SINGLE SVG RIBBON -->
              <svg :viewBox="`0 0 ${svgCanvasWidth} 450`" :style="{ width: svgCanvasWidth + 'px', height: '450px', position: 'absolute', top: 0, left: 0 }" class="ribbon-svg" preserveAspectRatio="none">
                <defs>
                  <linearGradient id="ribbonGrad" x1="0%" y1="100%" x2="0%" y2="0%">
                    <stop offset="0%" stop-color="#FDE68A" stop-opacity="1"/>
                    <stop offset="50%" stop-color="#F59E0B" stop-opacity="1"/>
                    <stop offset="100%" stop-color="#EF4444" stop-opacity="1"/>
                  </linearGradient>
                </defs>
                
                <path :d="smoothRibbonPath" fill="none" stroke="rgba(0,0,0,0.06)" stroke-width="24" stroke-linecap="round" transform="translate(0, 12)" />
                <path :d="smoothRibbonPath" fill="none" stroke="url(#ribbonGrad)" stroke-width="24" stroke-linecap="round" />
                
                <path v-if="futureNodeCoords && rightmostNodeCoords" 
                      :d="`M ${rightmostNodeCoords.cx} ${rightmostNodeCoords.cy} C ${rightmostNodeCoords.cx + 150} ${rightmostNodeCoords.cy}, ${futureNodeCoords.cx - 150} ${futureNodeCoords.cy}, ${futureNodeCoords.cx} ${futureNodeCoords.cy}`" 
                      fill="none" stroke="#D1D5DB" stroke-width="4" stroke-dasharray="6,6" stroke-linecap="round" />
                <circle v-if="futureNodeCoords" :cx="futureNodeCoords.cx" :cy="futureNodeCoords.cy" r="6" fill="#8B5CF6" />
              </svg>

              <!-- 2. THE HTML CARDS LAYER -->
              <div class="ribbon-nodes-layer" :style="{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }">
                
                <div v-for="(node, index) in processedHistory" 
                    :key="'hist-'+index" 
                    class="history-node-wrapper"
                    :style="{ 
                      left: node.cx + 'px', 
                      top: node.cy + 'px',
                      zIndex: hoveredHistoryIndex === index ? 30 : 20 - index,
                      pointerEvents: 'auto'
                    }"
                    :class="{ 'is-hovered': hoveredHistoryIndex === index, 'is-dimmed': hoveredHistoryIndex !== null && hoveredHistoryIndex !== index }"
                    @click.stop="selectedHistoryNode = node">
                  
                  <div class="history-dot">
                    <div class="dot-inner" :class="{ 'current': node.current }"></div>
                    <div class="dot-halo" v-if="hoveredHistoryIndex === index"></div>
                  </div>
                  
                  <div class="history-card" :class="node.align">
                    <span class="term-badge">{{ node.dates }}</span>
                    <span class="current-badge" v-if="node.current">{{ $t('orgDashboard.userDashboard.careerSimulator.now') }}</span>
                    
                    <!-- PEAK BADGE -->
                    <span v-if="node.isPeak" style="position: absolute; top: -12px; left: 50%; transform: translateX(-50%); background: #EF4444; color: white; padding: 2px 8px; border-radius: 999px; font-size: 0.65rem; font-weight: 800; letter-spacing: 0.05em; white-space: nowrap; box-shadow: 0 4px 6px rgba(239, 68, 68, 0.3); z-index: 10;">
                      {{ $t('orgDashboard.userDashboard.careerSimulator.careerPeak') }}
                    </span>

                    <h3 class="mt-2">{{ node.title }}</h3>
                    <p class="org">{{ node.company }}</p>
                    <div class="skills-row mt-3"><span v-for="s in node.skills.slice(0,4)" :key="s" class="mini-skill">{{ s }}</span></div>
                    <div class="card-details-reveal">
                      <p class="desc mt-3">{{ node.impact_narrative }}</p>
                      <div class="footer-meta mt-3"><span>⚡ {{ $t('orgDashboard.userDashboard.careerSimulator.complexity', { score: node.role_complexity_score }) }}</span></div>
                    </div>
                  </div>
                </div>

                <!-- FUTURE NODE -->
                <div v-if="futureNodeCoords" class="history-node-wrapper"
                    :style="{ left: futureNodeCoords.cx + 'px', top: futureNodeCoords.cy + 'px', pointerEvents: 'none' }">
                  <div class="history-card up" style="border-top-color: #8B5CF6; border-top-width: 6px; box-shadow: 0 10px 25px rgba(139, 92, 246, 0.15);">
                    <span class="term-badge" style="background:#EDE9FE; color:#6D28D9; border: 1px solid #DDD6FE;">{{ $t('orgDashboard.userDashboard.careerSimulator.aiPredictedNextStep') }}</span>
                    <h3 class="mt-3" style="color: #6D28D9; font-size: 1.1rem; font-weight: 800;">{{ ultimateFutureRole }}</h3>
                    <p class="org">{{ $t('orgDashboard.userDashboard.careerSimulator.externalMarketGoal') }}</p>
                    <p class="desc mt-3" style="font-style: italic; font-size: 0.75rem;">{{ $t('orgDashboard.userDashboard.careerSimulator.futureNodeDesc') }}</p>
                  </div>
                </div>

              </div>
            </div>
            
            <div class="scrub-hint">{{ $t('orgDashboard.userDashboard.careerSimulator.scrubHint') }}</div>
          </div>
        </div>
      </div>
    </transition>

    <transition name="fade">
      <div v-if="showHistoryModal && processedHistory.length === 0" class="custom-error-modal-overlay ribbon-modal-overlay" style="z-index: 99999;" @click.self="showHistoryModal = false">
        <div style="background: white; padding: 2.5rem; border-radius: 16px; text-align: center; max-width: 400px; box-shadow: 0 10px 25px rgba(0,0,0,0.1);">
          <div style="font-size: 3rem; margin-bottom: 1rem;">📭</div>
          <h3 style="margin: 0 0 0.5rem 0; font-weight: 700;">{{ $t('orgDashboard.userDashboard.careerSimulator.noCareerHistoryTitle') }}</h3>
          <p style="color: #6B7280; font-size: 0.9rem;">{{ $t('orgDashboard.userDashboard.careerSimulator.noCareerHistoryDesc') }}</p>
          <button class="btn-secondary mt-4" style="width: 100%;" @click="showHistoryModal = false">{{ $t('orgDashboard.userDashboard.careerSimulator.close') }}</button>
        </div>
      </div>
    </transition>

    <!-- Wizard Content Area -->
    <main class="wizard-content" :class="{ 'no-scroll': currentStep <= 5 }">
      <transition name="fade" mode="out-in">
        
        <!-- STEP 1: CV Upload & Parse -->
        <div v-if="currentStep === 1" class="step-layout" key="step1">
          <div class="left-aligned-header">
            <h1 class="huge-title font-bold">{{ $t('orgDashboard.userDashboard.careerSimulator.startWithCv') }}</h1>
            <p class="subtitle mb-6">{{ $t('orgDashboard.userDashboard.careerSimulator.startWithCvDesc') }}</p>
          </div>

          <div class="cv-split-view">
            
            <!-- LEFT SIDE: Upload Box OR Radar -->
            <div class="upload-box" :class="{ 'is-dragover': isDragging, 'is-parsing': isParsing, 'is-success': !isParsing && parsedData }" @dragover.prevent="isDragging = true" @dragleave.prevent="isDragging = false" @drop.prevent="handleDrop">
              <input type="file" ref="fileInput" @change="handleFileSelect" accept=".pdf,.doc,.docx" hidden />
              <!-- 1. Default Upload State -->
              <div v-if="!isParsing && !parsedData" class="upload-content">
                <div class="upload-icon">↑</div>
                <h3>{{ $t('orgDashboard.userDashboard.careerSimulator.uploadYourResume') }}</h3>
                <p>{{ $t('orgDashboard.userDashboard.careerSimulator.dragDropBrowse') }}</p>

                <button class="btn-secondary mt-4" @click="$refs.fileInput.click()">{{ $t('orgDashboard.userDashboard.careerSimulator.selectFile') }}</button>
              </div>

              <!-- 2. DEEP SCAN RADAR STATE -->
              <div v-else-if="isParsing" class="radar-parsing-state">
                <div class="radar-document-container">
                  
                  <!-- Roaming Magnifying Glass -->
                  <div class="mag-glass-wrapper">
                    <div class="mag-glass"></div>
                    <div class="mag-handle"></div>
                  </div>

                  <!-- Skeleton UI -->
                  <div class="sk-header">
                    <div class="sk-avatar"></div>
                    <div class="sk-lines">
                      <div class="sk-line w-60"></div>
                      <div class="sk-line w-40"></div>
                    </div>
                  </div>
                  <div class="sk-body">
                    <div class="sk-line w-90"></div>
                    <div class="sk-line w-80"></div>
                    <div class="sk-line w-85"></div>
                    <div class="sk-line w-50 mt-3"></div>
                    <div class="sk-line w-75"></div>
                    <div class="sk-line w-80 mt-3"></div>
                    <div class="sk-line w-60"></div>
                  </div>

                  <!-- The Sweeping Laser -->
                  <div class="scanner-laser-group">
                    <div class="scanner-gradient"></div>
                    <div class="scanner-line"></div>
                  </div>

                  <!-- Dynamic Floating Badges with Connectors -->
                  <div class="float-badge badge-1">
                    <span class="badge-dot"></span> 🔍 Tech Stack
                    <div class="connector-line left-line"></div>
                  </div>
                  <div class="float-badge badge-2">
                    <div class="connector-line right-line"></div>
                    ⚡ Velocity <span class="badge-dot"></span>
                  </div>
                  <div class="float-badge badge-3">
                    <span class="badge-dot"></span> 🧠 HR Intel
                    <div class="connector-line left-line"></div>
                  </div>
                  <div class="float-badge badge-4">
                    <div class="connector-line right-line"></div>
                    🎯 Trajectories <span class="badge-dot"></span>
                  </div>
                </div>

                <h3 class="radar-title">{{ $t('orgDashboard.userDashboard.careerSimulator.deepAiScan') }}</h3>
                <p class="radar-status-text">{{ $t(currentRadarStatus) }}</p>
              </div>

              <!-- 3. Success State (Abstract Resume Preview) -->
              <div v-else class="success-state-wrapper">
                
                <div class="resume-preview-card clickable-resume" @click="viewResume()">
                  
                  <!-- TEMPLATE 1: The Split Sidebar -->
                  <div v-if="randomResumeTemplate === 1" class="resume-skeleton layout-split">
                    <div class="sk-sidebar">
                      <div class="sk-circle"></div>
                      <div class="sk-line w-full mt-4"></div>
                      <div class="sk-line w-80 mt-2"></div>
                      <div class="sk-line w-60 mt-2"></div>
                      <div class="sk-accent w-full mt-6"></div>
                      <div class="sk-line w-full mt-2"></div>
                      <div class="sk-line w-80 mt-2"></div>
                      <div class="sk-line w-90 mt-2"></div>
                      <div class="sk-accent w-full mt-6"></div>
                      <div class="sk-line w-75 mt-2"></div>
                      <div class="sk-line w-60 mt-2"></div>
                    </div>
                    <div class="sk-main">
                      <div class="sk-accent w-60 h-6"></div>
                      <div class="sk-line w-40 mt-2 mb-6"></div>
                      
                      <div class="sk-section-title"></div>
                      <div class="sk-line w-full mt-3"></div>
                      <div class="sk-line w-90 mt-2"></div>
                      <div class="sk-line w-80 mt-2 mb-5"></div>
                      
                      <div class="sk-section-title"></div>
                      <div class="sk-line w-full mt-3"></div>
                      <div class="sk-line w-85 mt-2"></div>
                      <div class="sk-line w-90 mt-2"></div>
                      <div class="sk-line w-75 mt-2 mb-5"></div>

                      <div class="sk-section-title"></div>
                      <div class="sk-line w-full mt-3"></div>
                      <div class="sk-line w-80 mt-2"></div>
                    </div>
                  </div>

                  <!-- TEMPLATE 2: The Modern Centered -->
                  <div v-if="randomResumeTemplate === 2" class="resume-skeleton layout-centered">
                    <div class="sk-header-centered">
                      <div class="sk-circle-sm"></div>
                      <div class="sk-accent w-40 h-5 mt-3"></div>
                      <div class="sk-line w-60 mt-2"></div>
                      <div class="sk-line w-50 mt-2"></div>
                    </div>
                    <div class="sk-body-grid mt-6">
                      <div class="sk-col">
                        <div class="sk-section-title"></div>
                        <div class="sk-line w-full mt-3"></div>
                        <div class="sk-line w-80 mt-2"></div>
                        <div class="sk-line w-90 mt-2 mb-4"></div>
                        
                        <div class="sk-section-title"></div>
                        <div class="sk-line w-full mt-3"></div>
                        <div class="sk-line w-75 mt-2"></div>
                        <div class="sk-line w-85 mt-2 mb-4"></div>

                        <div class="sk-section-title"></div>
                        <div class="sk-line w-full mt-3"></div>
                      </div>
                      <div class="sk-col">
                        <div class="sk-section-title"></div>
                        <div class="sk-line w-full mt-3"></div>
                        <div class="sk-line w-85 mt-2"></div>
                        <div class="sk-line w-75 mt-2 mb-4"></div>
                        
                        <div class="sk-section-title"></div>
                        <div class="sk-line w-full mt-3"></div>
                        <div class="sk-line w-90 mt-2"></div>
                        <div class="sk-line w-80 mt-2 mb-4"></div>

                        <div class="sk-section-title"></div>
                        <div class="sk-line w-full mt-3"></div>
                      </div>
                    </div>
                  </div>

                  <!-- TEMPLATE 3: The Executive Minimalist -->
                  <div v-if="randomResumeTemplate === 3" class="resume-skeleton layout-executive">
                    <div class="sk-top-bar"></div>
                    <div class="sk-exec-header mt-4">
                      <div class="sk-accent w-50 h-6"></div>
                      <div class="sk-line w-30 mt-2"></div>
                      <div class="sk-line w-40 mt-2"></div>
                    </div>
                    <div class="sk-exec-body mt-6">
                      <div class="sk-flex-row">
                        <div class="sk-line w-20"></div>
                        <div class="sk-line w-75"></div>
                      </div>
                      <div class="sk-flex-row mt-3">
                        <div class="sk-line w-20"></div>
                        <div class="sk-block w-75 h-16"></div>
                      </div>
                      
                      <div class="sk-flex-row mt-5">
                        <div class="sk-line w-20"></div>
                        <div class="sk-line w-75"></div>
                      </div>
                      <div class="sk-flex-row mt-3">
                        <div class="sk-line w-20"></div>
                        <div class="sk-block w-75 h-16"></div>
                      </div>

                      <div class="sk-flex-row mt-5">
                        <div class="sk-line w-20"></div>
                        <div class="sk-line w-75"></div>
                      </div>
                      <div class="sk-flex-row mt-3">
                        <div class="sk-line w-20"></div>
                        <div class="sk-block w-75 h-10"></div>
                      </div>
                    </div>
                  </div>

                  <!-- The Floating Success Overlay -->
                  <div class="success-floating-panel">
                    <div class="panel-left">
                      <div class="success-icon-sm">✓</div>
                      <div class="panel-text">
                        <h4>{{ $t('orgDashboard.userDashboard.careerSimulator.activeDocument') }}</h4>
                        <p>{{ uploadedFileName || $t('orgDashboard.userDashboard.careerSimulator.loadedFromIntelligenceDb') }}</p>
                      </div>
                    </div>
                    
                    <div class="change-btn" @click.stop="$refs.fileInput.click()">{{ $t('orgDashboard.userDashboard.careerSimulator.changeFile') }}</div>
                  </div>

                </div>
              </div>
            </div>

            <transition name="fade">
              <div v-if="showResumeModal" class="custom-error-modal-overlay" style="z-index: 999999;" @click.self="showResumeModal = false">
                <div class="resume-pdf-modal">
                  <div class="modal-header" style="display: flex; justify-content: space-between; align-items: center; padding-bottom: 1rem; border-bottom: 1px solid #E5E7EB; margin-bottom: 1rem;">
                    <div>
                      <h3 style="margin: 0; font-size: 1.2rem; color: #111827;">{{ $t('orgDashboard.userDashboard.careerSimulator.uploadedResumeTitle') }}</h3>
                      <p style="margin: 0; font-size: 0.8rem; color: #6B7280;">{{ uploadedFileName || $t('orgDashboard.userDashboard.careerSimulator.storedInDb') }}</p>
                    </div>
                    <button class="close-btn" style="font-size: 1.5rem;" @click="showResumeModal = false">×</button>
                  </div>
                  
                  <div class="pdf-container" style="width: 100%; height: 70vh; background: #F3F4F6; border-radius: 8px; overflow: hidden; display: flex; align-items: center; justify-content: center;">
                    <iframe v-if="resumePdfUrl" :src="resumePdfUrl" width="100%" height="100%" style="border: none;"></iframe>
                    <div v-else class="loader-spinner"></div>
                  </div>
                </div>
              </div>
            </transition>

            <!-- RIGHT SIDE: Skeleton Loader OR Parsed Data -->
            <transition name="fade">
              <div class="parsed-preview-box skeleton-box" v-if="isParsing && parsingStage >= 2">
                
                <!-- HEADER AREA -->
                <div class="preview-header pb-4" style="border-bottom: 1px solid #F3F4F6;">
                  <div class="profile-info">
                    <div v-if="mockDataPhase < 1" class="sk-pulse-circle"></div>
                    <div v-else class="avatar-circle anim-pop-in" style="background:#E5E7EB; color:#9CA3AF; border-color:#F3F4F6;">...</div>
                    
                    <div v-if="mockDataPhase < 1">
                      <div class="sk-pulse-line w-24 mb-2"></div>
                      <div class="sk-pulse-line w-40"></div>
                    </div>
                    <div v-else class="anim-slide-right">
                      <span class="eyebrow" style="color:#D1D5DB;">{{ $t('orgDashboard.userDashboard.careerSimulator.identifyingCandidate') }}</span>
                      <h4 style="color:#9CA3AF; font-size:1.1rem; margin:0;">{{ $t('orgDashboard.userDashboard.careerSimulator.parsingIdentity') }}</h4>
                    </div>
                  </div>
                  <div v-if="mockDataPhase >= 1" class="velocity-badge anim-pop-in" style="background:#F9FAFB; color:#9CA3AF; border-color:#E5E7EB;">
                    {{ $t('orgDashboard.userDashboard.careerSimulator.careerVelocity') }} <span style="font-style:italic;">{{ $t('orgDashboard.userDashboard.careerSimulator.scanning') }}</span>
                  </div>
                  <div v-else class="sk-pulse-line w-32" style="height: 24px; border-radius: 12px;"></div>
                </div>

                <!-- SKILLS AREA -->
                <div class="preview-section mt-5">
                  <h5 style="color:#D1D5DB !important;">{{ $t('orgDashboard.userDashboard.careerSimulator.extractingSkills') }}</h5>
                  <div class="tags-container" v-if="mockDataPhase >= 2">
                    <span class="skill-tag anim-pop-in" style="animation-delay: 0.0s; color:#9CA3AF !important;">Cloud Architecture</span>
                    <span class="skill-tag anim-pop-in" style="animation-delay: 0.1s; color:#9CA3AF !important;">System Design</span>
                    <span class="skill-tag anim-pop-in" style="animation-delay: 0.2s; color:#9CA3AF !important;">Problem Solving</span>
                    <span class="skill-tag anim-pop-in" style="animation-delay: 0.3s; color:#9CA3AF !important;">API Integration</span>
                    <span class="skill-tag anim-pop-in" style="animation-delay: 0.4s; background:#F9FAFB; border:1px dashed #D1D5DB; color:#D1D5DB !important;">{{ $t('orgDashboard.userDashboard.careerSimulator.scanning') }}</span>
                  </div>
                  <div class="tags-container" v-else>
                    <div class="sk-pulse-tag"></div><div class="sk-pulse-tag w-l"></div>
                    <div class="sk-pulse-tag"></div><div class="sk-pulse-tag w-s"></div>
                  </div>
                </div>

                <!-- ROLES AREA -->
                <div class="preview-section mt-5">
                  <h5 style="color:#D1D5DB !important;">{{ $t('orgDashboard.userDashboard.careerSimulator.simulatingRoleFit') }}</h5>
                  <div class="role-bars" v-if="mockDataPhase >= 3">
                    <div class="role-bar-item anim-slide-up" style="animation-delay: 0.0s;">
                      <span class="role-title" style="color:#9CA3AF;">{{ $t('orgDashboard.userDashboard.careerSimulator.primaryPathway') }}</span>
                      <div class="bar-wrap"><div class="bar-fill anim-fill-bar" style="width: 88%; filter: grayscale(1) opacity(0.4);"></div></div>
                    </div>
                    <div class="role-bar-item anim-slide-up" style="animation-delay: 0.1s;">
                      <span class="role-title" style="color:#9CA3AF;">{{ $t('orgDashboard.userDashboard.careerSimulator.secondaryTarget') }}</span>
                      <div class="bar-wrap"><div class="bar-fill anim-fill-bar" style="width: 75%; filter: grayscale(1) opacity(0.4);"></div></div>
                    </div>
                    <div class="role-bar-item anim-slide-up" style="animation-delay: 0.2s;">
                      <span class="role-title" style="color:#9CA3AF;">{{ $t('orgDashboard.userDashboard.careerSimulator.stretchRole') }}</span>
                      <div class="bar-wrap"><div class="bar-fill anim-fill-bar" style="width: 60%; filter: grayscale(1) opacity(0.4);"></div></div>
                    </div>
                  </div>
                  <div v-else>
                     <div class="sk-pulse-line w-full mb-3 h-8"></div>
                     <div class="sk-pulse-line w-full mb-3 h-8"></div>
                     <div class="sk-pulse-line w-full h-8"></div>
                  </div>
                </div>
              </div>

              <!-- ACTUAL PARSED DATA -->
              <div class="parsed-preview-box" v-else-if="parsedData">
                <div class="preview-header" style="display: flex; justify-content: space-between; align-items: flex-start; padding-bottom: 1rem; border-bottom: 1px solid #F3F4F6;">
                  <div class="profile-info">
                      <div class="avatar-circle">
                        <img v-if="parsedData.avatar" :src="parsedData.avatar" />
                        <span v-else>{{ parsedData.initials }}</span>
                      </div>
                      <div>
                        <span class="eyebrow">{{ $t('orgDashboard.userDashboard.careerSimulator.parsedScan') }}</span>
                        <h4>{{ parsedData.name }}</h4>
                        <p>{{ parsedData.email }}</p>
                      </div>
                  </div>
                  <div class="velocity-badge custom-tooltip-parent" style="position: relative; cursor: help; margin-top: 4px;">
                    {{ $t('orgDashboard.userDashboard.careerSimulator.careerVelocity') }} <strong style="margin-left: 4px; text-transform: capitalize;">{{ parsedData.learning_velocity || parsedData.velocity || $t('orgDashboard.userDashboard.careerSimulator.scanning') }}</strong>
                    
                    <span class="tooltip-text velocity-tooltip">
                      {{ parsedData.learning_velocity_reasoning || parsedData.velocityReasoning || 'Rapid skill acquisition based on project progression.' }}
                    </span>
                  </div>
                </div>

                <div class="preview-section mt-4">
                  <h5>{{ $t('orgDashboard.userDashboard.careerSimulator.topSkills') }}</h5>
                  <div class="tags-container">
                    <span class="skill-tag" v-for="skill in (showAllSkills ? parsedData.skills : parsedData.skills.slice(0, 8))" :key="skill.name">
                      {{ skill.name }} <span class="percent">{{ skill.percentage }}%</span>
                    </span>
                    <span v-if="!showAllSkills && parsedData.skills.length > 8" class="skill-tag overflow-tag clickable-tag" @click="showAllSkills = true">
                      {{ $t('orgDashboard.userDashboard.careerSimulator.moreCount', { count: parsedData.skills.length - 8 }) }}
                    </span>
                    <span v-if="showAllSkills && parsedData.skills.length > 8" class="skill-tag overflow-tag clickable-tag" @click="showAllSkills = false">
                      {{ $t('orgDashboard.userDashboard.careerSimulator.showLess') }}
                    </span>
                  </div>
                </div>

                <div class="preview-section mt-5">
                  <h5>{{ $t('orgDashboard.userDashboard.careerSimulator.bestFitRolesTitle') }}</h5>
                  <div class="role-bars">
                    <div class="role-bar-item" v-for="role in parsedData.bestFitRoles" :key="role.title">
                      <span class="role-title" :title="role.title">{{ role.title }}</span>
                      <div class="bar-wrap"><div class="bar-fill" :style="{ width: role.match + '%' }"></div></div>
                      <span class="match-text">{{ role.match }}%</span>
                    </div>
                  </div>
                </div>
              </div>
            </transition>

          </div>
        </div>

        <!-- STEP 2: Type of Work -->
       <div v-if="currentStep === 2" class="step-pane centered-pane" key="step2">
          <div class="form-card left-aligned-card">
            
            <div style="display: flex; align-items: center; gap: 1rem; margin-bottom: 1.5rem;">
              <div class="step-icon-sm" style="margin: 0; flex-shrink: 0;">🎯</div>
              <div>
                <h1 class="huge-title font-bold" style="margin: 0;">{{ $t('orgDashboard.userDashboard.careerSimulator.typeOfWorkTitle') }}</h1>
                <p class="subtitle" style="margin: 0; margin-top: 0.25rem;">{{ $t('orgDashboard.userDashboard.careerSimulator.typeOfWorkSubtitle') }}</p>
              </div>
            </div>
            
            <div class="input-group">
              <select v-model="formData.targetAreaBase" class="pill-select" :class="{'active-border': formData.targetAreaBase}">
                <option value="" disabled>{{ $t('orgDashboard.userDashboard.careerSimulator.selectTargetArea') }}</option>
                <option v-for="opt in computedTargetOptions" :key="'target-'+opt" :value="opt">
                  {{ translateTargetOption(opt) }}
                </option>
                <!-- Restored "Other" option -->
                <option value="Other">{{ $t('orgDashboard.userDashboard.careerSimulator.otherWriteOwn') }}</option>
              </select>
              
              <transition name="slide-down">
                <input v-if="formData.targetAreaBase === 'Other'" v-model="formData.targetAreaCustom" type="text" class="pill-input-dashed mt-3" :placeholder="$t('orgDashboard.userDashboard.careerSimulator.typeCustomTarget')" />
              </transition>
            </div>
                        
            <p class="description-text mt-6">{{ $t('orgDashboard.userDashboard.careerSimulator.typeOfWorkExamples') }}</p>
          </div>
        </div>

        <!-- STEP 3: Industry -->
        <div v-if="currentStep === 3" class="step-pane centered-pane" key="step3">
          <div class="form-card left-aligned-card">
            
            <div style="display: flex; align-items: center; gap: 1rem; margin-bottom: 1.5rem;">
              <div class="step-icon-sm" style="margin: 0; flex-shrink: 0;">🏢</div>
              <div>
                <h1 class="huge-title font-bold" style="margin: 0;">{{ $t('orgDashboard.userDashboard.careerSimulator.industryFocusTitle') }}</h1>
                <p class="subtitle" style="margin: 0; margin-top: 0.25rem;">{{ $t('orgDashboard.userDashboard.careerSimulator.industryFocusSubtitle') }}</p>
              </div>
            </div>

            <div class="input-group">
              <select v-model="formData.industryBase" class="pill-select" :class="{'active-border': formData.industryBase}">
                <option value="" disabled>{{ $t('orgDashboard.userDashboard.careerSimulator.selectIndustryFocus') }}</option>
                <option v-for="opt in computedIndustryOptions" :key="'ind-'+opt" :value="opt">
                  {{ translateIndustryOption(opt) }}
                </option>
                <!-- Restored "Other" option -->
                <option value="Other">{{ $t('orgDashboard.userDashboard.careerSimulator.otherWriteOwn') }}</option>
              </select>
              
              <transition name="slide-down">
                <input v-if="formData.industryBase === 'Other'" v-model="formData.industryCustom" type="text" class="pill-input-dashed mt-3" :placeholder="$t('orgDashboard.userDashboard.careerSimulator.typeCustomIndustry')" />
              </transition>
            </div>
          </div>
        </div>

        <!-- STEP 4: Supporting Skills -->
        <div v-if="currentStep === 4" class="step-pane centered-pane" key="step4">
          <div class="form-card left-aligned-card">
            
            <div style="display: flex; align-items: center; gap: 1rem; margin-bottom: 1.5rem;">
              <div class="step-icon-sm" style="margin: 0; flex-shrink: 0;">🛠️</div>
              <div>
                <h1 class="huge-title font-bold" style="margin: 0;">{{ $t('orgDashboard.userDashboard.careerSimulator.supportingSkillsTitle') }}</h1>
                <p class="subtitle" style="margin: 0; margin-top: 0.25rem;">{{ $t('orgDashboard.userDashboard.careerSimulator.supportingSkillsSubtitle') }}</p>
              </div>
            </div>

            <div class="pills-container left-align-pills mt-4">
              <div v-for="skillName in displaySupportingSkills" 
                   :key="'sup-'+skillName" 
                   :class="['skill-pill-outline', { active: formData.supportingSkills.includes(skillName) }]"
                   @click="toggleSkill('supportingSkills', skillName)">
                {{ skillName }}
              </div>
            </div>
            
            <input v-model="newSupportingSkill" @keydown.enter.prevent="addCustomSkill('supportingSkills', newSupportingSkill)" class="pill-input-dashed mt-6" :placeholder="$t('orgDashboard.userDashboard.careerSimulator.typeCustomSkill')" />
          </div>
        </div>

        <!-- STEP 5: Alternate Skills -->
        <div v-if="currentStep === 5" class="step-pane centered-pane" key="step5">
          <div class="form-card left-aligned-card">
            
            <div style="display: flex; align-items: center; gap: 1rem; margin-bottom: 1.5rem;">
              <div class="step-icon-sm" style="margin: 0; flex-shrink: 0;">💡</div>
              <div>
                <h1 class="huge-title font-bold" style="margin: 0;">{{ $t('orgDashboard.userDashboard.careerSimulator.alternateSkillsTitle') }}</h1>
                <p class="subtitle" style="margin: 0; margin-top: 0.25rem;">{{ $t('orgDashboard.userDashboard.careerSimulator.alternateSkillsSubtitle') }}</p>
              </div>
            </div>

            <div class="pills-container left-align-pills mt-4">
               <div v-for="skillName in displayAlternateSkills" 
                   :key="'alt-'+skillName" 
                   :class="['skill-pill-outline', { active: formData.alternateSkills.includes(skillName) }]"
                   @click="toggleSkill('alternateSkills', skillName)">
                {{ skillName }}
              </div>
            </div>
            
            <input v-model="newAlternateSkill" @keydown.enter.prevent="addCustomSkill('alternateSkills', newAlternateSkill)" class="pill-input-dashed mt-6" :placeholder="$t('orgDashboard.userDashboard.careerSimulator.typeCustomStretchSkill')" />
          </div>
        </div>

        <!-- STEP 6: Chart / Kanban -->
        <div v-if="currentStep === 6" class="step-layout" key="step6">
          <div class="step-header-box">
            <h1 class="huge-title font-bold">{{ $t('orgDashboard.userDashboard.careerSimulator.alternatePathwaysTitle') }}</h1>
            <p class="subtitle mb-6">
              {{ $t('orgDashboard.userDashboard.careerSimulator.alternatePathwaysSubtitle', { area: finalTargetArea || 'AI & Machine Learning' }) }}
            </p>
            
            <!-- Controls -->
            <div class="controls-row mt-4" style="display: flex; justify-content: space-between; align-items: center; width: 100%;">
              <div class="toggle-group">
                <button class="toggle-btn" :class="{ active: readinessTab === 'skill' }" @click="readinessTab = 'skill'">
                  {{ $t('orgDashboard.userDashboard.careerSimulator.skillReadiness') }}
                </button>
                <button class="toggle-btn" :class="{ active: readinessTab === 'time' }" @click="readinessTab = 'time'">
                  {{ $t('orgDashboard.userDashboard.careerSimulator.timeReadiness') }}
                </button>
              </div>
              
              <div class="custom-tooltip-parent tiny-toggle-wrapper" @click="opportunityScope = opportunityScope === 'org' ? 'overall' : 'org'">
                <div class="tiny-switch" :class="{ 'active': opportunityScope === 'overall' }">
                  <div class="tiny-knob">
                    <svg v-if="opportunityScope === 'org'" xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="4" y="2" width="16" height="20" rx="2" ry="2"></rect><path d="M9 22v-4h6v4"></path></svg>
                    <svg v-else xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="2" y1="12" x2="22" y2="12"></line></svg>
                  </div>
                </div>
                <span class="tooltip-text" style="bottom: 140%; right: 0; left: auto; transform: none; white-space: nowrap;">
                  {{ opportunityScope === 'org' ? $t('orgDashboard.userDashboard.careerSimulator.viewingInternalTooltip') : $t('orgDashboard.userDashboard.careerSimulator.viewingMarketTooltip') }}
                </span>
              </div>
            </div>
          </div>


          <!-- TAB 1: SANKEY CHART -->
          <div v-show="readinessTab === 'skill'" class="step-content-card chart-wrapper" ref="chartWrapper">
            <div class="chart-headers">
              <span class="header-col-1">{{ $t('orgDashboard.userDashboard.careerSimulator.targetFunctionalArea') }}</span>
              <span class="header-col-2">{{ $t('orgDashboard.userDashboard.careerSimulator.careerTitlesProfession') }}</span>
              <span class="header-col-3">{{ $t('orgDashboard.userDashboard.careerSimulator.requiredSkills') }}</span>
            </div>

            <svg class="chart-svg-layer" preserveAspectRatio="none">
              <path v-for="link in chartLinks.rootToRoles" :key="link.id" :d="link.path" class="chart-line root-line" :class="{ 'dimmed': hoveredRole !== null && hoveredRole !== link.roleId }" />
              <path v-for="link in chartLinks.rolesToSkills" :key="link.id" :d="link.path" class="chart-line skill-line" :style="{ strokeWidth: (link.width || 2.5) + 'px' }" :class="[link.status, { 'dimmed': hoveredRole !== null && hoveredRole !== link.roleId, 'highlighted': hoveredRole === link.roleId }]" />
            </svg>

            <!-- Nodes Layer -->
            <div class="chart-nodes-layer">
              <div class="chart-col col-root">
                <div class="root-node-container" style="display: flex; align-items: center; gap: 0.8rem;">
                  <div class="custom-tooltip-parent">
                    <div class="history-trigger-dot" @click="showHistoryModal = true">
                       <span class="dot-icon">🕒</span>
                    </div>
                    <span class="tooltip-text left-tooltip">{{ $t('orgDashboard.userDashboard.careerSimulator.viewCareerHistoryTooltip') }}</span>
                  </div>
                  <div class="chart-node node-root" ref="rootNode">
                    <span class="node-label highlight-text">{{ finalTargetArea || $t('orgDashboard.userDashboard.careerSimulator.targetArea') }}</span>
                    <div class="node-dot bg-orange shadow-dot"></div>
                  </div>
                </div>
              </div>

              <div class="chart-col col-roles">
                <div class="chart-node node-role" v-for="(role, index) in allChartRoles" :key="'role-'+index" 
                     :ref="'roleNode-'+index" @mouseenter="hoveredRole = index" @mouseleave="hoveredRole = null" 
                     @click.stop="openRoleDetails(role, $event)">
                  <div class="node-dot border-orange"></div>
                  <div class="role-text-wrap">
                    <span class="node-label title">{{ role.title }}</span>
                    <span class="node-meta">
                      {{ $t('orgDashboard.userDashboard.careerSimulator.roleNodeMeta', { fit: role.fit, time: role.time }) }}
                    </span>
                  </div>
                  <div class="node-dot border-orange right-dot"></div>
                </div>
              </div>

              <div class="chart-col col-skills">
                <div class="chart-node node-skill" v-for="(skill, index) in allChartSkills" :key="'skill-'+index"
                     :ref="'skillNode-'+index" :class="{'dimmed': hoveredRole !== null && !isSkillAssociated(skill.name, hoveredRole)}">
                  <div class="node-dot shadow-dot" :class="skill.status === 'have' ? 'border-green' : 'border-orange'"></div>
                  <span class="node-label">{{ skill.name }}</span>
                </div>
              </div>
            </div>

            <div class="chart-legend">
              <div class="legend-left">
                <span class="legend-item"><div class="node-dot bg-green"></div> {{ $t('orgDashboard.userDashboard.careerSimulator.have') }}</span>
                <span class="legend-item"><div class="node-dot bg-yellow"></div> {{ $t('orgDashboard.userDashboard.careerSimulator.stretchWillingToLearn') }}</span>
                <span class="legend-item"><div class="node-dot bg-orange"></div> {{ $t('orgDashboard.userDashboard.careerSimulator.gap') }}</span>
              </div>
              <span class="legend-hint">{{ $t('orgDashboard.userDashboard.careerSimulator.hoverNodeHint') }}</span>
            </div>
          </div>

          <!-- TAB 2: TIME KANBAN -->
          <div v-show="readinessTab === 'time'" class="time-readiness-view mt-6">
  
            <!-- TOP BANNER -->
            <div class="trajectory-header-labels">
              <span class="text-purple-700 font-bold uppercase tracking-widest text-xs">{{ $t('orgDashboard.userDashboard.careerSimulator.shortTerm') }}</span>
              <span class="text-purple-700 font-bold uppercase tracking-widest text-xs">{{ $t('orgDashboard.userDashboard.careerSimulator.longTerm') }}</span>
            </div>

            <div class="trajectory-banner mt-2">
              <div class="banner-title-text">{{ $t('orgDashboard.userDashboard.careerSimulator.growthBanner') }}</div>
              
              <div class="trajectory-graph-area">
                <span class="today-label">{{ $t('orgDashboard.userDashboard.careerSimulator.today') }}</span>
                
                <svg class="trajectory-lines-svg" viewBox="0 0 100 100" preserveAspectRatio="none">
                  <path v-for="(node, i) in timeTrajectoryNodes" 
                        :key="'svg-line-'+i"
                        class="trajectory-path"
                        :d="node.pathData" 
                        fill="none" />
                </svg>

                <div class="graph-nodes-layer">
                  <div class="node-root-you" style="top: 80%; left: 6%;">YOU</div>
                  
                  <!-- Dynamic Nodes -->
                  <div class="scatter-nodes">
                    <div class="scatter-node-wrapper" 
                        v-for="(node, i) in timeTrajectoryNodes" 
                        :key="'scatter-'+i"
                        :style="{ top: node.cy + '%', left: node.cx + '%' }"
                        @click.stop="openKanbanRoleDetails(node, node.timeGroup, $event)">
                      
                      <!-- Label BELOW the dot for even items -->
                      <div v-if="i % 2 === 0" class="node-text-label label-below">
                        <strong>{{ node.title }}</strong>
                        <span>{{ finalTargetArea || $t('orgDashboard.userDashboard.careerSimulator.department') }}</span>
                      </div>

                      <!-- The Node Dot -->
                      <div class="node-time-circle">{{ node.time || '~4mo' }}</div>
                      
                      <!-- Label ABOVE the dot for odd items -->
                      <div v-if="i % 2 !== 0" class="node-text-label label-above">
                        <strong>{{ node.title }}</strong>
                        <span>{{ finalTargetArea || $t('orgDashboard.userDashboard.careerSimulator.department') }}</span>
                      </div>

                    </div>
                  </div>
                </div>
              </div>
            </div>
            <!-- BOTTOM 3 COLUMNS -->
            <div class="time-action-columns mt-6">
              
              <!-- Col 1: Risk (Ready Now) -->
              <div class="action-col">
                <div class="col-header-area">
                  <div class="purple-bracket">{{ $t('orgDashboard.userDashboard.careerSimulator.identificationActions') }}</div>
                  <h3 class="col-main-title text-green-500">{{ $t('orgDashboard.userDashboard.careerSimulator.risk') }}</h3>
                  <span class="col-sub-title">{{ $t('orgDashboard.userDashboard.careerSimulator.readyNowMonths') }}</span>
                </div>
                <div class="action-box bg-green-light border-green-light">
                  <p class="box-intro-text">{{ $t('orgDashboard.userDashboard.careerSimulator.riskDesc') }}</p>
                  
                  <div class="role-cards-wrapper" v-if="careerPaths.readyNow && careerPaths.readyNow.length">
                    <div class="action-role-card" v-for="(path, index) in careerPaths.readyNow" :key="'now-'+index"
                         @click.stop="openKanbanRoleDetails(path, 'readyNow', $event)">
                      <span class="card-role-title">{{ path.title }}</span>
                      <span class="card-role-time">{{ path.time || '0mo' }}</span>
                    </div>
                  </div>
                  <div class="role-cards-wrapper empty" v-else>
                    <p>{{ $t('orgDashboard.userDashboard.careerSimulator.emptyPaths') }}</p>
                  </div>
                </div>
              </div>

              <!-- Col 2: Potential (Ready Shortly) -->
              <div class="action-col">
                <div class="col-header-area">
                  <div class="purple-bracket">{{ $t('orgDashboard.userDashboard.careerSimulator.developmentAction') }}</div>
                  <h3 class="col-main-title text-yellow-500">{{ $t('orgDashboard.userDashboard.careerSimulator.potential') }}</h3>
                  <span class="col-sub-title">{{ $t('orgDashboard.userDashboard.careerSimulator.readyShortlyMonths') }}</span>
                </div>
                <div class="action-box bg-yellow-light border-yellow-light">
                  <p class="box-intro-text">{{ $t('orgDashboard.userDashboard.careerSimulator.potentialDesc') }}</p>
                  
                  <div class="role-cards-wrapper" v-if="careerPaths.readyShortly && careerPaths.readyShortly.length">
                    <div class="action-role-card" v-for="(path, index) in careerPaths.readyShortly" :key="'shortly-'+index"
                         @click.stop="openKanbanRoleDetails(path, 'readyShortly', $event)">
                      <span class="card-role-title">{{ path.title }}</span>
                      <span class="card-role-time">{{ path.time || '~4mo' }}</span>
                    </div>
                  </div>
                  <div class="role-cards-wrapper empty" v-else>
                    <p>{{ $t('orgDashboard.userDashboard.careerSimulator.emptyPaths') }}</p>
                  </div>
                </div>
              </div>

              <!-- Col 3: Success (Ready Later) -->
              <div class="action-col">
                <div class="col-header-area">
                  <div class="purple-bracket">{{ $t('orgDashboard.userDashboard.careerSimulator.opportunityAllocation') }}</div>
                  <h3 class="col-main-title text-orange-500">{{ $t('orgDashboard.userDashboard.careerSimulator.success') }}</h3>
                  <span class="col-sub-title">{{ $t('orgDashboard.userDashboard.careerSimulator.readyLaterMonths') }}</span>
                </div>
                <div class="action-box bg-orange-light border-orange-light">
                  <p class="box-intro-text">{{ $t('orgDashboard.userDashboard.careerSimulator.successDesc') }}</p>
                  
                  <div class="role-cards-wrapper" v-if="careerPaths.readyLater && careerPaths.readyLater.length">
                    <div class="action-role-card" v-for="(path, index) in careerPaths.readyLater" :key="'later-'+index"
                         @click.stop="openKanbanRoleDetails(path, 'readyLater', $event)">
                      <span class="card-role-title">{{ path.title }}</span>
                      <span class="card-role-time">{{ path.time || '~9mo' }}</span>
                    </div>
                  </div>
                  <div class="role-cards-wrapper empty" v-else>
                    <p>{{ $t('orgDashboard.userDashboard.careerSimulator.emptyPaths') }}</p>
                  </div>
                </div>
              </div>

            </div>
          </div>

        </div>

        <!-- STEP 7: Open Roles Map -->
        <div v-if="currentStep === 7" class="step-layout" key="step7">
          <div class="step-header-box" style="display: flex; justify-content: space-between; align-items: flex-start; width: 100%;">
            <div>
              <h1 class="huge-title font-bold">{{ $t('orgDashboard.userDashboard.careerSimulator.openRolesTitle') }}</h1>
              <p class="subtitle mb-6">{{ $t('orgDashboard.userDashboard.careerSimulator.openRolesSubtitle') }}</p>
            </div>
          </div>

          <div class="roles-grid" v-if="openRoles && openRoles.length > 0">
            <div class="open-role-card" v-for="(role, index) in openRoles" :key="'role-'+index">
              <div class="card-head">
                <div>
                  <span class="dept">
                    <strong v-if="role.org_name || role.organization_name" style="color:#D97706; margin-right:4px;">
                      {{ role.org_name || role.organization_name }} •
                    </strong> 
                    {{ role.department || $t('orgDashboard.userDashboard.careerSimulator.none') }}
                  </span>
                  <h3>{{ role.title }}</h3>
                  <span class="meta">{{ role.meta }}</span>
                </div>
                <div class="match-badge" :class="role.match_percent >= 80 ? 'high' : 'medium'">
                  {{ $t('orgDashboard.userDashboard.careerSimulator.matchPercent', { percent: role.match_percent }) }}
                </div>
              </div>
              <div class="match-bar-wrap"><div class="match-bar" :style="{ width: role.match_percent + '%' }"></div></div>
              
              <p class="mapped-text mt-3">
                {{ $t('orgDashboard.userDashboard.careerSimulator.mappedFromPath', { path: role.mapped_from_path }) }}
              </p>
              
              <div class="skills-block mt-4" v-if="role.skills_have && role.skills_have.length">
                <h6>{{ $t('orgDashboard.userDashboard.careerSimulator.youHave') }}</h6>
                <div class="skill-chips has">
                  <span v-for="skill in role.skills_have" :key="'have-'+skill">{{ skill }}</span>
                </div>
              </div>
              
              <div class="skills-block mt-4" v-if="role.skills_missing && role.skills_missing.length">
                <h6>{{ $t('orgDashboard.userDashboard.careerSimulator.stillMissing') }}</h6>
                <div class="skill-chips missing">
                  <span v-for="skill in role.skills_missing" :key="'miss-'+skill">{{ skill }}</span>
                </div>
              </div>

              <!-- HIDE REQUEST BUTTON FOR EXTERNAL ROLES -->
              <div class="action-area mt-5 pt-4 text-right" style="border-top: 1px solid #F3F4F6;" v-if="opportunityScope === 'org'">
                 <button class="btn-secondary" 
                         @click="requestRoleAssignment(role, index)" 
                         :disabled="role.isRequesting || role.requestSuccess || role.requestStatus === 'rejected'"
                         :style="getButtonStyle(role)">
                    <span v-if="role.isRequesting">
                      <div class="btn-spinner" style="border-color: rgba(0,0,0,0.2); border-top-color: #374151;"></div> {{ $t('orgDashboard.userDashboard.careerSimulator.requesting') }}
                    </span>
                    <span v-else-if="role.requestStatus === 'accepted'">{{ $t('orgDashboard.userDashboard.careerSimulator.acceptedByAdmin') }}</span>
                    <span v-else-if="role.requestStatus === 'rejected'">{{ $t('orgDashboard.userDashboard.careerSimulator.declined') }}</span>
                    <span v-else-if="role.requestStatus === 'pending' || role.requestSuccess">{{ $t('orgDashboard.userDashboard.careerSimulator.requestSent') }}</span>
                    <span v-else>{{ $t('orgDashboard.userDashboard.careerSimulator.requestJoin') }}</span>
                 </button>
              </div>
            </div>
          </div>
          <div class="empty-roles-state step-content-card" style="text-align: center; padding: 4rem;" v-else>
             <div class="icon-placeholder" style="font-size: 3rem; opacity: 0.3; margin-bottom: 1rem;">🏢</div>
             <h3 style="font-size: 1.2rem; font-weight: 600; margin-bottom: 0.5rem;">{{ $t('orgDashboard.userDashboard.careerSimulator.noExactMatchesTitle') }}</h3>
             <p style="color: #6B7280;">{{ $t('orgDashboard.userDashboard.careerSimulator.noExactMatchesDesc') }}</p>
          </div>
        </div>

        <!-- STEP 8: Career Blueprint -->
        <div v-if="currentStep === 8" class="step-pane centered-pane" key="step8">
          <div class="form-card left-aligned-card">
            
            <!-- HEADER ALIGNED WITH ICON ON LEFT -->
            <div style="display: flex; align-items: center; gap: 1rem; margin-bottom: 1.5rem; width: 100%;">
              <div class="step-icon-sm" style="margin: 0; flex-shrink: 0;">📄</div>
              <div>
                <h1 class="huge-title font-bold" style="margin: 0;">Your Career Blueprint</h1>
                <p class="subtitle" style="margin: 0; margin-top: 0.25rem; color: #6B7280;">
                  Download your comprehensive 6-part AI report mapping out your journey to <strong>{{ ultimateFutureRole || 'your target role' }}</strong>.
                </p>
              </div>
            </div>
            
            <div class="selected-box text-left" style="width: 100%; border: 1px solid #E5E7EB; margin-bottom: 2rem;">
              <h3 style="font-size: 1rem; margin-bottom: 0.75rem; color: #111827; font-weight: 700;">What's Inside:</h3>
              <ul style="font-size: 0.9rem; color: #4B5563; padding-left: 1.2rem; line-height: 1.8; margin: 0;">
                <li><strong>Career Journey Overview</strong> & Transition Metrics</li>
                <li><strong>Career Path Visualization</strong> (External Market)</li>
                <li><strong>Skill Gap Analysis</strong> & Priority Matrix</li>
                <li><strong>Step-by-Step Learning Roadmap</strong></li>
                <li><strong>Role Progression Timeline</strong></li>
                <li><strong>Top 5 Immediate Action Steps</strong></li>
              </ul>
            </div>

            <button class="btn-primary" style="width: 100%; padding: 1.2rem; font-size: 1.1rem; margin-bottom: 1rem;" @click="generateAndDownloadBlueprint" :disabled="isGeneratingBlueprint">
              <span v-if="isGeneratingBlueprint" style="display: flex; align-items: center; justify-content: center; gap: 8px;">
                <div class="btn-spinner"></div> Assembling Report...
              </span>
              <span v-else>Download Full Blueprint PDF</span>
            </button>
            
            <div style="text-align: center; width: 100%;">
              <button class="btn-text" style="font-size: 0.85rem;" @click="finishWizard">Skip and go to Dashboard →</button>
            </div>
          </div>
        </div>

      </transition>
    </main>

    <!-- Floating Popups Layer -->
    <transition name="zoom">
      <div class="role-detail-popup floating-popup" v-if="selectedRoleDetails && currentStep === 6" :style="popupStyle" @click.stop v-click-outside="closePopup">
        <div class="popup-header">
          <span class="dept">{{ finalTargetArea || $t('orgDashboard.userDashboard.careerSimulator.department') }}</span>
          <button class="close-btn" @click.stop="selectedRoleDetails = null">×</button>
        </div>
        <div class="popup-title-row">
          <h3>{{ selectedRoleDetails.title }}</h3>
          <span class="term-badge">{{ selectedRoleDetails.time }}</span>
        </div>
        <div class="fit-section mt-4">
          <span>{{ $t('orgDashboard.userDashboard.careerSimulator.overallFit') }}</span>
          <span>{{ $t('orgDashboard.userDashboard.careerSimulator.matchPercent', { percent: selectedRoleDetails.fit }) }}</span>
        </div>
        <div class="match-bar-wrap"><div class="match-bar" :style="{ width: selectedRoleDetails.fit + '%' }"></div></div>
        <div class="skills-split mt-4">
          <div class="skills-block">
            <h6>{{ $t('orgDashboard.userDashboard.careerSimulator.youAlreadyHave') }}</h6>
            <div class="skill-chips has">
              <span v-for="s in selectedRoleDetails.skillsHave" :key="'h-'+s">{{ s }}</span>
              <span v-if="selectedRoleDetails.skillsHave && selectedRoleDetails.skillsHave.length===0" class="empty-chip">{{ $t('orgDashboard.userDashboard.careerSimulator.none') }}</span>
            </div>
          </div>
          <div class="skills-block">
            <h6>{{ $t('orgDashboard.userDashboard.careerSimulator.gapToClose') }}</h6>
            <div class="skill-chips missing">
              <span v-for="s in selectedRoleDetails.skillsMissing" :key="'m-'+s">{{ s }}</span>
              <span v-if="selectedRoleDetails.skillsMissing && selectedRoleDetails.skillsMissing.length===0" class="empty-chip">{{ $t('orgDashboard.userDashboard.careerSimulator.none') }}</span>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <!-- Sticky Footer Nav -->
    <footer class="wizard-footer">
      <div class="footer-inner">
        <div class="footer-left">
          <button class="btn-text" @click="prevStep" :disabled="currentStep === 1">{{ $t('orgDashboard.userDashboard.careerSimulator.back') }}</button>
        </div>
        
        <!-- STEPPER DESIGN: Icons Only, centered -->
        <div class="footer-stepper">
          <div 
            v-for="(step, index) in steps" 
            :key="index"
            class="stepper-icon-item"
            :class="{ 'active': currentStep === step.id, 'completed': currentStep > step.id }"
            @click="goToStep(step.id)"
          >
            <div class="stepper-dot-icon" v-html="step.icon"></div>
            <div class="stepper-tooltip">{{ getStepLabel(step) }}</div>
          </div>
        </div>

        <div class="footer-right">
          <button v-if="currentStep < 5" class="btn-primary" @click="nextStep">{{ $t('orgDashboard.userDashboard.careerSimulator.nextStep') }}</button>
          <button v-else-if="currentStep === 5" class="btn-primary generate-btn" @click="generatePredictions" :disabled="isPredicting">
            <span v-if="isPredicting"><div class="btn-spinner"></div> {{ $t('orgDashboard.userDashboard.careerSimulator.generating') }}</span>
            <span v-else>{{ $t('orgDashboard.userDashboard.careerSimulator.seePredictions') }}</span>
          </button>
          <button v-else-if="currentStep === 6" class="btn-primary" @click="nextStep">{{ $t('orgDashboard.userDashboard.careerSimulator.matchOpenRoles') }}</button>
          <button v-else class="btn-primary finish-btn" @click="finishWizard">{{ $t('orgDashboard.userDashboard.careerSimulator.downloadProfile') }}</button>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
import axios from 'axios';
import html2canvas from 'html2canvas';

const clickOutside = {
  bind: function (el, binding, vnode) {
    el.clickOutsideEvent = function (event) {
      if (!(el == event.target || el.contains(event.target))) {
        vnode.context[binding.expression](event);
      }
    };
    document.body.addEventListener('click', el.clickOutsideEvent);
  },
  unbind: function (el) {
    document.body.removeEventListener('click', el.clickOutsideEvent);
  },
};

export default {
  name: 'CareerSimulator',
  directives: { clickOutside },
  data() {
    return {
      isGeneratingBlueprint: false,
      isAppReady: false,
      captureTimeout: null,
      capturedChartImage: null,
      needsRefetch: true,
      showAllSkills: false,
      errorMessage: '',
      currentStep: 1, 
      isDragging: false,
      isParsing: false,
      isPredicting: false,
      uploadedFile: null,
      uploadedFileName: '',
      parsedData: null, 
      saveTimeout: null,
      zoomLevel: 1,
      isPanning: false,
      startX: 0,
      startY: 0,
      panX: 0,
      panY: 0,
      currentRadarStatus: "orgDashboard.userDashboard.careerSimulator.radarInitializing",
      parsingStage: 1,
      randomResumeTemplate: 1,
      showHistoryModal: false, 
      showResumeModal: false,
      resumePdfUrl: null,
      dynamicTargetOptions: [],
      dynamicIndustryOptions: [],
      dynamicSupportingSkills: [],
      dynamicAlternateSkills: [],
      ultimateFutureRole: "",
      processedHistory: [],
      totalCareerDuration: '',
      internalData: { careerPaths: null, openRoles: [] },
      externalData: { careerPaths: null, openRoles: [] },
      careerPaths: { readyNow: [], readyShortly: [], readyLater: [] },
      openRoles: [],
      steps: [
        { id: 1, icon: '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg>' }, 
        { id: 2, icon: '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><circle cx="12" cy="12" r="6"></circle><circle cx="12" cy="12" r="2"></circle></svg>' }, 
        { id: 3, icon: '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="4" y="2" width="16" height="20" rx="2" ry="2"></rect><path d="M9 22v-4h6v4"></path><path d="M8 6h.01"></path><path d="M16 6h.01"></path><path d="M12 6h.01"></path><path d="M12 10h.01"></path><path d="M12 14h.01"></path><path d="M16 10h.01"></path><path d="M16 14h.01"></path><path d="M8 10h.01"></path><path d="M8 14h.01"></path></svg>' },
        { id: 4, icon: '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>' }, 
        { id: 5, icon: '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"></path></svg>' },
        { id: 6, icon: '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76"></polygon></svg>' }, 
        { id: 7, icon: '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path></svg>' },
        { id: 8, icon: '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="12" y1="18" x2="12" y2="12"></line><line x1="9" y1="15" x2="15" y2="15"></line></svg>' }
      ],
      targetOptions: ["AI & Machine Learning", "Backend Development", "Software Engineering", "Data & Analytics", "Infrastructure", "Architecture & Consulting", "Other"],
      industryOptions: ["SaaS", "FinTech", "HealthTech", "E-commerce", "Consulting", "Other"],
      formData: {
        targetAreaBase: '', targetAreaCustom: '',
        industryBase: '', industryCustom: '',
        supportingSkills: [], alternateSkills: []
      },
      newSupportingSkill: '',
      newAlternateSkill: '',
      fallbackSkills: [],
      recommendationSkills: [],
      hoveredHistoryIndex: null,
      selectedHistoryNode: null,
      readinessTab: 'time',
      opportunityScope: 'org', 
      hoveredRole: null,
      selectedRoleDetails: null,
      popupStyle: { top: '0px', left: '0px', position: 'absolute' },
      allChartRoles: [], allChartSkills: [], chartLinks: { rootToRoles: [], rolesToSkills: [] },
    };
  },
  computed: {
    finalTargetArea() { return this.formData.targetAreaBase === 'Other' ? this.formData.targetAreaCustom : this.formData.targetAreaBase; },
    finalIndustry() { return this.formData.industryBase === 'Other' ? this.formData.industryCustom : this.formData.industryBase; },
    computedTargetOptions() {
      return (this.dynamicTargetOptions && this.dynamicTargetOptions.length > 0) 
             ? this.dynamicTargetOptions 
             : this.targetOptions;
    },
    computedIndustryOptions() {
      return (this.dynamicIndustryOptions && this.dynamicIndustryOptions.length > 0) 
             ? this.dynamicIndustryOptions 
             : this.industryOptions;
    },
    displaySupportingSkills() {
      let baseList = [];
      if (this.dynamicSupportingSkills && this.dynamicSupportingSkills.length > 0) {
        baseList = this.dynamicSupportingSkills;
      } else if (this.parsedData && this.parsedData.skills && this.parsedData.skills.length > 0) {
        baseList = this.parsedData.skills.map(s => s.name);
      } else {
        baseList = this.fallbackSkills.map(s => s.name);
      }
      const combined = new Set([...baseList, ...this.formData.supportingSkills]);
      return Array.from(combined);
    },
    displayAlternateSkills() {
      let baseList = [];
      if (this.dynamicAlternateSkills && this.dynamicAlternateSkills.length > 0) {
        baseList = this.dynamicAlternateSkills;
      } else {
        baseList = this.recommendationSkills;
      }
      const combined = new Set([...baseList, ...this.formData.alternateSkills]);
      return Array.from(combined);
    },
    rightmostNodeCoords() {
        if (!this.processedHistory || this.processedHistory.length === 0) return null;
        return this.processedHistory.reduce((prev, current) => (prev.cx > current.cx) ? prev : current);
    },
    futureNodeCoords() {
        if (!this.rightmostNodeCoords) return null;
        return {
            cx: this.rightmostNodeCoords.cx + 350,
            cy: Math.max(80, this.rightmostNodeCoords.cy - 70)
        };
    },
    smoothRibbonPath() {
        if (!this.processedHistory || this.processedHistory.length === 0) return '';
        let path = `M -50 ${this.processedHistory[0].cy}`;
        let prevX = -50;
        let prevY = this.processedHistory[0].cy;

        this.processedHistory.forEach(node => {
            const controlPointOffset = (node.cx - prevX) / 2;
            path += ` C ${prevX + controlPointOffset} ${prevY}, ${node.cx - controlPointOffset} ${node.cy}, ${node.cx} ${node.cy}`;
            prevX = node.cx;
            prevY = node.cy;
        });
        return path;
    },
    timeTrajectoryNodes() {
        const paths = [
            ...(this.careerPaths.readyNow || []).map(p => ({...p, timeGroup: 'readyNow'})),
            ...(this.careerPaths.readyShortly || []).map(p => ({...p, timeGroup: 'readyShortly'})),
            ...(this.careerPaths.readyLater || []).map(p => ({...p, timeGroup: 'readyLater'}))
        ];

        const total = paths.length;
        if (total === 0) return [];

        // Expanded bounds to maximize horizontal spacing
        const startX = 18;
        const endX = 94;
        const stepX = total > 1 ? (endX - startX) / (total - 1) : 0;

        let nowCount = 0;
        let shortlyCount = 0;
        let laterCount = 0;

        return paths.map((path, i) => {
            const cx = total === 1 ? 50 : startX + (i * stepX);
            
            // Dynamic climbing vertical positions
            let cy;
            if (path.timeGroup === 'readyNow') {
                cy = 66 - (nowCount * 5); 
                nowCount++;
            } else if (path.timeGroup === 'readyShortly') {
                cy = 44 - (shortlyCount * 5); 
                shortlyCount++;
            } else {
                cy = 22 - (laterCount * 5); 
                laterCount++;
            }

            const startY = 80;
            const startXCoord = 6;

            // Clean Bezier logic to build smooth parabolic arches
            const cp1x = startXCoord + (cx - startXCoord) * 0.35;
            const cp1y = startY - (startY - cy) * 0.25; 
            
            const cp2x = cx - (cx - startXCoord) * 0.35;
            const cp2y = cy; 

            return {
                ...path,
                cx,
                cy,
                pathData: `M ${startXCoord} ${startY} C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${cx} ${cy}`
            };
        });
    }
  },
  watch: {
    currentStep(newVal) {
      this.triggerDbSave(); 
      if (newVal === 6) {
        this.readinessTab = 'skill';
        this.generateChartData();
        this.$nextTick(() => { 
          // INCREASED TIMEOUT: The "out-in" transition takes ~200ms to clear the old step.
          // Wait 350ms to ensure the Step 6 DOM is fully mounted and has proper dimensions
          // before calculating the precise coordinates for the SVG paths.
          setTimeout(this.drawLines, 350); 
        });
      }
    },
    formData: { 
      deep: true, 
      handler() { 
        this.triggerDbSave(); 
        this.needsRefetch = true;
      } 
    },
    careerPaths: { 
      deep: true, 
      handler() { 
        this.triggerDbSave(); 
      } 
    },
    openRoles: { 
      deep: true, 
      handler() { 
        this.triggerDbSave(); 
      } 
    },
    parsedData: { 
      deep: true, 
      handler() { 
        this.triggerDbSave(); 
      } 
    },
    readinessTab(newVal) {
      if (newVal === 'skill') {
        this.$nextTick(() => { 
          // Added a small delay here as well so `v-show` layout calculation is fully applied
          setTimeout(this.drawLines, 100);
        });
      }
    },
    opportunityScope(newValue) {
      this.updateActiveView();
    }
  },
  async mounted() {
    // 1. Load saved DB state FIRST
    await this.loadStateFromDB();
    
    // 2. Fetch fresh user data SECOND
    await this.forceFetchUserData();
    
    // 3. Only move to step 2 if they are literally just starting
    if (this.parsedData && this.currentStep === 1) {
        this.currentStep = 2;
    }
    
    // 4. CRITICAL: Allow saving to DB ONLY AFTER loading is complete
    this.isAppReady = true; 
    
    this.resumeRolePolling();
    window.addEventListener('resize', this.drawLines);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.drawLines);
  },
  methods: {
    getStepLabel(step) {
      const keys = {
        1: 'startWithCv',
        2: 'typeOfWorkTitle',
        3: 'industryFocusTitle',
        4: 'supportingSkillsTitle',
        5: 'alternateSkillsTitle',
        6: 'careerTrajectory',
        7: 'openRolesTitle',
        8: 'downloadProfile'
      };
      return this.$t(`orgDashboard.userDashboard.careerSimulator.${keys[step.id]}`);
    },
    translateTargetOption(opt) {
      if (!opt) return "";
      const keyMap = {
        "AI & Machine Learning": "aiMachineLearning",
        "Backend Development": "backendDevelopment",
        "Software Engineering": "softwareEngineering",
        "Data & Analytics": "dataAnalytics",
        "Infrastructure": "infrastructure",
        "Architecture & Consulting": "architectureConsulting",
        "Other": "otherWriteOwn"
      };
      const key = keyMap[opt];
      return key ? this.$t(`orgDashboard.userDashboard.careerSimulator.${key}`) : opt;
    },
    translateIndustryOption(opt) {
      const keyMap = {
        "SaaS": "saas",
        "FinTech": "fintech",
        "HealthTech": "healthtech",
        "E-commerce": "ecommerce",
        "Consulting": "consulting",
        "Other": "otherWriteOwn"
      };
      const key = keyMap[opt];
      return key ? this.$t(`orgDashboard.userDashboard.careerSimulator.${key}`) : opt;
    },
    getLocalUser() {
      try {
        const userStr = localStorage.getItem('user');
        if (userStr) {
           const u = JSON.parse(userStr);
           u.id = u._id || u.id || localStorage.getItem('userId');
           return u;
        }
        return { 
            id: localStorage.getItem('userId'), 
            email: localStorage.getItem('email') 
        };
      } catch (e) {
          console.error("Error parsing user from localStorage", e);
      }
      return { id: null, email: null };
    },
    getApiToken() { return localStorage.getItem('accessToken') || 'INTERNAL_SYSTEM_TOKEN'; },

    startParsingAnimation() {
      this.parsingStage = 1;
      this.mockDataPhase = 0;
      this.currentRadarStatus = "orgDashboard.userDashboard.careerSimulator.radarStructuring";
      
      setTimeout(() => { 
        if (this.isParsing) {
          this.parsingStage = 2;
          this.currentRadarStatus = "orgDashboard.userDashboard.careerSimulator.radarSkeptical"; 
        }
      }, 3500);

      setTimeout(() => { 
        if (this.isParsing) {
          this.mockDataPhase = 1;
          this.currentRadarStatus = "orgDashboard.userDashboard.careerSimulator.radarExtracting"; 
        }
      }, 5000);

      setTimeout(() => { 
        if (this.isParsing) {
          this.mockDataPhase = 2;
          this.currentRadarStatus = "orgDashboard.userDashboard.careerSimulator.radarSimulating"; 
        }
      }, 6500);
      
      setTimeout(() => { 
        if (this.isParsing) {
          this.mockDataPhase = 3;
          this.currentRadarStatus = "orgDashboard.userDashboard.careerSimulator.radarFinalizing"; 
        }
      }, 8000);
    },
    triggerDbSave() {
      // Prevents the app from overwriting Step 8 with Step 2 while loading!
      if (!this.isAppReady) return; 
      
      if (this.saveTimeout) clearTimeout(this.saveTimeout);
      this.saveTimeout = setTimeout(() => { this.saveStateToDB(); }, 1000);
    },

    async viewResume() {
      this.showResumeModal = true; 
      const user = this.getLocalUser();
      
      if (this.resumePdfUrl) return; 

      try {
        const safeEmail = encodeURIComponent(user.email);
        const response = await axios.get(`${process.env.VUE_APP_API_URL}/cv/${safeEmail}`, {
          headers: { Authorization: `Bearer ${this.getApiToken()}` },
          responseType: 'blob'
        });

        const file = new Blob([response.data], { type: 'application/pdf' });
        this.resumePdfUrl = URL.createObjectURL(file);

      } catch (error) {
        console.error("Failed to load resume PDF:", error);
        this.showResumeModal = false;
        this.triggerErrorToast(this.$t('orgDashboard.userDashboard.careerSimulator.failedResumeLoad'));
      }
    },

    toggleSkill(listName, skillName) {
      const idx = this.formData[listName].indexOf(skillName);
      if (idx > -1) this.formData[listName].splice(idx, 1);
      else this.formData[listName].push(skillName);
      
      this.needsRefetch = true; 
      this.triggerDbSave();
    },

    addCustomSkill(listName, newSkill) {
      if (newSkill && newSkill.trim() !== '') {
        const trimmed = newSkill.trim();
        if (!this.formData[listName].includes(trimmed)) this.formData[listName].push(trimmed);
        
        if (listName === 'supportingSkills') {
          const listToUpdate = (this.parsedData && this.parsedData.skills) ? this.parsedData.skills : this.fallbackSkills;
          if (!listToUpdate.some(s => s.name.toLowerCase() === trimmed.toLowerCase())) {
            listToUpdate.push({ name: trimmed, percentage: 100 });
          }
          this.newSupportingSkill = '';
        }
        if (listName === 'alternateSkills') {
          if (!this.recommendationSkills.some(s => s.toLowerCase() === trimmed.toLowerCase())) {
            this.recommendationSkills.push(trimmed);
          }
          this.newAlternateSkill = '';
        }
        
        this.needsRefetch = true;
        this.triggerDbSave();
      }
    },

    zoomIn() { if (this.zoomLevel < 2.0) this.zoomLevel += 0.2; },
    zoomOut() { if (this.zoomLevel > 0.6) this.zoomLevel -= 0.2; },
    resetZoom() { this.zoomLevel = 1; this.panX = 0; this.panY = 0; },

    startPan(e) {
      this.isPanning = true;
      this.startX = e.clientX - this.panX;
      this.startY = e.clientY - this.panY;
    },

    async forceFetchUserData() {
      const user = this.getLocalUser();

      if (!user || (!user.id && !user.email)) {
        this.processCareerTimeline([], []);
        return;
      }

      try {
        const apiToken = this.getApiToken();
        
        try {
            const defaultsRes = await axios.get(`${process.env.VUE_APP_API_URL}/api/career-simulator/defaults`, {
                params: { email: user.email },
                headers: { Authorization: `Bearer ${apiToken}` }
            });
            if (defaultsRes.data) {
                this.dynamicTargetOptions = defaultsRes.data.target_areas || [];
                this.dynamicIndustryOptions = defaultsRes.data.industries || [];
                this.dynamicSupportingSkills = defaultsRes.data.supporting_skills || [];
                this.dynamicAlternateSkills = defaultsRes.data.alternate_skills || [];
                this.ultimateFutureRole = defaultsRes.data.ultimate_future_role || "Future Leader";

                if (!this.formData.targetAreaBase || !this.dynamicTargetOptions.includes(this.formData.targetAreaBase)) {
                    this.formData.targetAreaBase = ""; 
                }
                if (!this.formData.industryBase || !this.dynamicIndustryOptions.includes(this.formData.industryBase)) {
                    this.formData.industryBase = ""; 
                }
                
                if (this.formData.supportingSkills.length === 0 && this.dynamicSupportingSkills.length > 0) {
                    this.formData.supportingSkills = [...this.dynamicSupportingSkills.slice(0, 5)];
                }
                if (this.formData.alternateSkills.length === 0 && this.dynamicAlternateSkills.length > 0) {
                    this.formData.alternateSkills = [...this.dynamicAlternateSkills.slice(0, 3)];
                }

                this.triggerDbSave();
            }
        } catch (e) {
            console.warn("No personalized defaults found. Using fallbacks.");
        }

        const response = await axios.get(`${process.env.VUE_APP_API_URL}/api/employee-intelligence`, { 
            headers: { Authorization: `Bearer ${apiToken}` },
            params: {
                email: user.email, 
                user_id: user.id
            }
        });
        
        const dbData = response.data;
        
        if (dbData && dbData.career_timeline && dbData.career_timeline.length > 0) {
          this.processCareerTimeline(dbData.career_timeline, dbData.project_complexity || []);
        } else {
          this.processCareerTimeline([], []);
        }

        this.parsedData = {
            ...this.parsedData,
            rawTimeline: dbData.career_timeline,
            rawProjects: dbData.project_complexity
        };

        if (dbData && dbData.skill_depth) {
            let parsedSkills = [];
            try {
                const rawSkills = typeof dbData.skill_depth === 'string' ? JSON.parse(dbData.skill_depth) : dbData.skill_depth;
                parsedSkills = rawSkills.map(s => ({ name: s.skill || s.name, percentage: Math.round((s.confidence || 0.85) * 100) }));
                parsedSkills.sort((a,b) => b.percentage - a.percentage);
            } catch (e) {}

            let bestRoles = [];
            try {
                const rr = typeof dbData.role_readiness === 'string' ? JSON.parse(dbData.role_readiness) : (dbData.role_readiness || {});
                bestRoles = Object.entries(rr).map(([title, match]) => ({ title, match: Math.round(match * 100) })).sort((a,b) => b.match - a.match).slice(0, 4);
            } catch(e) {}

            let displayName = dbData.name || this.parsedData?.name || user.name || user.firstName;
            
            if (!displayName && user.email) {
                const prefix = user.email.split('@')[0];
                displayName = prefix.charAt(0).toUpperCase() + prefix.slice(1).replace(/[^a-zA-Z0-9]/g, ' ');
            }
            this.parsedData = {
                ...this.parsedData,
                name: displayName || "Guest User",
                initials: (displayName || "GU").substring(0, 2).toUpperCase(),
                email: user.email,
                learning_velocity: dbData.learning_velocity || "Rapid",
                learning_velocity_reasoning: dbData.learning_velocity_reasoning || "Rapid skill acquisition based on project progression.",
                skills: parsedSkills,
                bestFitRoles: bestRoles,
            };
            this.randomResumeTemplate = Math.floor(Math.random() * 3) + 1;
            this.uploadedFileName = this.$t('orgDashboard.userDashboard.careerSimulator.loadedFromIntelligenceDb');
            
            if(this.currentStep === 1) this.currentStep = 2;
        }
      } catch (error) { 
        console.error("API call failed. Falling back to mock data.", error);
        this.processCareerTimeline([], []);
      }
    },

    processCareerTimeline(timeline, projects) {
        if (!timeline || timeline.length === 0) return;
        
        // ULTIMATE BULLETPROOF FRONTEND DATE PARSER
        // Used ONLY for calculating the "Total Career Duration" text.
        const parseDate = (dateStr) => {
            if (!dateStr || typeof dateStr !== 'string') return new Date();
            
            // Clean the string (remove ordinals like 'th', 'nd', and commas)
            const lowerStr = dateStr.toLowerCase().replace(/(st|nd|rd|th)\b/g, '').replace(/,/g, ' ').trim();
            
            // Handle current/present
            if (lowerStr.includes('present') || lowerStr.includes('now') || lowerStr.includes('current')) {
                return new Date();
            }

            // 1. Extract Year
            const yearMatch = lowerStr.match(/\b(19|20)\d{2}\b/);
            const year = yearMatch ? parseInt(yearMatch[0], 10) : new Date().getFullYear();

            // 2. Extract Month (Default to January / 0)
            let month = 0; 
            let foundMonth = false;
            
            // A. Try word months first (handles typos like 'apirl')
            const monthMap = { jan:0, feb:1, mar:2, apr:3, api:3, may:4, jun:5, jul:6, aug:7, sep:8, oct:9, nov:10, dec:11 };
            for (const [mName, mNum] of Object.entries(monthMap)) {
                if (lowerStr.includes(mName)) { 
                    month = mNum; 
                    foundMonth = true;
                    break; 
                }
            }

            // B. Try number formats if no word was found
            if (!foundMonth) {
                // Look specifically for MM/YYYY or MM-YYYY
                const mmYyyyMatch = lowerStr.match(/\b(\d{1,2})[\/\-](?:19|20)\d{2}\b/);
                if (mmYyyyMatch && parseInt(mmYyyyMatch[1], 10) <= 12) {
                    month = parseInt(mmYyyyMatch[1], 10) - 1;
                } else {
                    // Fallback: just grab the first valid 1-12 number in the string
                    const numbers = lowerStr.match(/\b\d{1,2}\b/g) || [];
                    for (let num of numbers) {
                        const n = parseInt(num, 10);
                        if (n >= 1 && n <= 12) {
                            month = n - 1;
                            break;
                        }
                    }
                }
            }
            
            return new Date(year, month, 1);
        };
        
        // 100% TRUST BACKEND ORDERING - NO .sort()
        const mappedTimeline = timeline.map(role => {
            const datesArr = role.dates ? role.dates.split(/–|-|to/).map(s => s.trim()) : [];
            return {
                ...role,
                startDate: parseDate(datesArr[0]),
                endDate: parseDate(datesArr[1] || 'Present'),
                skills: (role.tech_summary || "").split(',').map(s => s.trim()).filter(Boolean)
            };
        });

        if (mappedTimeline.length === 0) return;
      
        // Calculate Total Career Duration securely
        const firstStartDate = mappedTimeline[0].startDate;
        const lastEndDate = new Date(); // Today
        let totalMonths = (lastEndDate.getFullYear() - firstStartDate.getFullYear()) * 12 + (lastEndDate.getMonth() - firstStartDate.getMonth());
        totalMonths = Math.max(1, totalMonths); // Prevent negative or 0 duration
        this.totalCareerDuration = (totalMonths / 12).toFixed(1);

        let maxComplexity = -1;

        // Plot nodes sequentially from Left to Right
        const finalHistory = mappedTimeline.map((role, index) => {
            const complexity = parseFloat(role.role_complexity_score || 5.0);
            if (complexity > maxComplexity) maxComplexity = complexity;
            
            const cx = 150 + (index * 350); 
            const cy = 380 - ((complexity - 1) * (300 / 9)); 
            const align = cy > 225 ? 'down' : 'up'; 

            return {
                ...role,
                cx: Math.round(cx),
                cy: Math.round(cy),
                align: align,
                current: (role.dates || "").toLowerCase().includes('now') || (role.dates || "").toLowerCase().includes('present'),
                role_complexity_score: complexity.toFixed(1)
            };
        });

        finalHistory.forEach(node => {
            node.isPeak = parseFloat(node.role_complexity_score) === maxComplexity && maxComplexity > 3.0;
        });

        const maxCx = finalHistory.length > 0 ? finalHistory[finalHistory.length - 1].cx : 0;
        this.svgCanvasWidth = Math.max(1020, maxCx + 550);

        this.processedHistory = finalHistory;
        this.$forceUpdate();
    },
    scrubRibbon(e) {
      if (!this.$refs.ribbonArea) return;
      const historySource = this.processedHistory;

      if (this.isPanning) {
        this.panX = e.clientX - this.startX;
        this.panY = e.clientY - this.startY;
        return; 
      }
      
      const rect = this.$refs.ribbonArea.getBoundingClientRect();
      const mouseX = (e.clientX - rect.left - this.panX) / this.zoomLevel;
      const mouseY = (e.clientY - rect.top - this.panY) / this.zoomLevel;
      
      let closest = null; 
      let minDistance = Infinity;
      
      historySource.forEach((node, i) => {
        const distance = Math.hypot(node.cx - mouseX, node.cy - mouseY);
        if (distance < minDistance && distance < 150) { 
          minDistance = distance; 
          closest = i; 
        }
      });
      
      this.hoveredHistoryIndex = closest;
    },
    endPan() { this.isPanning = false; },

    resumeRolePolling() {
      const user = this.getLocalUser();
      if (!this.openRoles || !this.openRoles.length) return;
      
      this.openRoles.forEach((role, index) => {
        if (role.requestStatus === 'pending' || role.requestSuccess) {
          const pollInterval = setInterval(async () => {
            try {
                const roleIdToUse = role.role_id || role.id || role._id;
                const res = await axios.get(`${process.env.VUE_APP_API_URL}/api/role/request-status?user_id=${user.id}&role_id=${roleIdToUse}`);
                const status = res.data.request_status;
                if (status === 'assigned' || status === 'accepted') {
                    this.$set(this.openRoles[index], 'requestStatus', 'accepted');
                    clearInterval(pollInterval);
                } else if (status === 'rejected') {
                    this.$set(this.openRoles[index], 'requestStatus', 'rejected');
                    clearInterval(pollInterval);
                }
            } catch (err) {}
          }, 5000);
          this.$set(this.openRoles[index], 'pollInterval', pollInterval);
        }
      });
    },

    async saveStateToDB() {
      const user = this.getLocalUser();
      if (!user || !user.id) return;
      const stateToSave = {
        currentStep: this.currentStep, formData: this.formData,
        careerPaths: this.careerPaths, openRoles: this.openRoles,
        parsedData: this.parsedData, recommendationSkills: this.recommendationSkills,
        capturedChartImage: this.capturedChartImage // <--- ADD THIS
      };
      try {
        await axios.post(`${process.env.VUE_APP_API_URL}/api/career-simulator/session`, {
          user_id: user.id, email: user.email, session_data: stateToSave
        }, { headers: { Authorization: `Bearer ${this.getApiToken()}` } });
      } catch (err) { console.warn("Failed to backup session to DB"); }
    },

    async loadStateFromDB() {
      const user = this.getLocalUser();
      if (!user || !user.id) return false;
      try {
        const res = await axios.get(`${process.env.VUE_APP_API_URL}/api/career-simulator/session?user_id=${user.id}`, {
          headers: { Authorization: `Bearer ${this.getApiToken()}` }
        });
        if (res.data && res.data.data) {
          const parsed = res.data.data;
          this.currentStep = parsed.currentStep || 1;
          this.formData = parsed.formData || this.formData;
          if (!this.formData.targetAreaBase) this.formData.targetAreaBase = "";
          if (!this.formData.industryBase) this.formData.industryBase = "";
          this.careerPaths = parsed.careerPaths || this.careerPaths;
          this.openRoles = parsed.openRoles || this.openRoles;
          this.capturedChartImage = parsed.capturedChartImage || null; // <--- ADD THIS
          this.parsedData = { 
              ...(parsed.parsedData || {}), 
              ...(this.parsedData || {}) 
          };
          this.recommendationSkills = parsed.recommendationSkills || this.recommendationSkills;
          return true; 
        }
      } catch (e) { }
      return false; 
    },

    async requestRoleAssignment(role, index) {
      this.$set(this.openRoles[index], 'isRequesting', true);
      const user = this.getLocalUser();
      try {
        let currentOrgId = role.organization_id || role.org_id;
        let currentOrgName = role.organization_name || role.org_name;
        if (currentOrgId && !/^[a-fA-F0-9]{24}$/.test(currentOrgId)) {
            if (currentOrgName && /^[a-fA-F0-9]{24}$/.test(currentOrgName)) currentOrgId = currentOrgName; 
            else {
                try {
                   const orgData = JSON.parse(localStorage.getItem('preferred_org_selection'));
                   if (orgData && orgData.id) currentOrgId = orgData.id;
                } catch(e) {}
            }
        }
        if (!currentOrgId) currentOrgId = "default_org";
        const payload = {
          role_id: role.role_id || role.id || role._id, role_name: role.title, 
          user_id: user.id, requested_by: user.id, organization_id: currentOrgId,
          type: 'join_role_request', status: 'pending', score: role.match_percent || 85 
        };
        await axios.post(`${process.env.VUE_APP_API_URL}/api/role/request-join`, payload, { headers: { Authorization: `Bearer ${this.getApiToken()}` } });
        this.$set(this.openRoles[index], 'requestSuccess', true);
        this.$set(this.openRoles[index], 'requestStatus', 'pending');
        if (typeof window !== 'undefined' && window.Utils && typeof window.Utils.playBellSound === 'function') window.Utils.playBellSound(800, 840, 'sine');
        this.triggerDbSave(); 

        const pollInterval = setInterval(async () => {
            try {
                const res = await axios.get(`${process.env.VUE_APP_API_URL}/api/role/request-status?user_id=${user.id}&role_id=${payload.role_id}`);
                const status = res.data.request_status;
                if (status === 'assigned' || status === 'accepted') {
                    this.$set(this.openRoles[index], 'requestStatus', 'accepted');
                    this.triggerDbSave(); clearInterval(pollInterval);
                } else if (status === 'rejected') {
                    this.$set(this.openRoles[index], 'requestStatus', 'rejected');
                    this.triggerDbSave(); clearInterval(pollInterval);
                }
            } catch (err) {}
        }, 5000); 
        this.$set(this.openRoles[index], 'pollInterval', pollInterval);
      } catch (error) { 
        this.triggerErrorToast(this.$t('orgDashboard.userDashboard.careerSimulator.failedRequest'));
      } finally { this.$set(this.openRoles[index], 'isRequesting', false); }
    },

    getButtonStyle(role) {
      if (role.requestStatus === 'accepted') return 'border-color:#10B981; color:#10B981; background:#ECFDF5;';
      if (role.requestStatus === 'rejected') return 'border-color:#EF4444; color:#EF4444; background:#FEF2F2;';
      if (role.requestStatus === 'pending' || role.requestSuccess) return 'border-color:#F59E0B; color:#F59E0B; background:#FFFBEB;';
      return '';
    },
    
    goToStep(stepId) { if (stepId <= this.currentStep) this.currentStep = stepId; },
    nextStep() { if (this.currentStep < 8) this.currentStep++; },
    prevStep() { if (this.currentStep > 1) this.currentStep--; },
    
    handleDrop(e) { this.isDragging = false; if (e.dataTransfer.files.length > 0) this.uploadResume(e.dataTransfer.files[0]); },
    handleFileSelect(e) { if (e.target.files.length > 0) this.uploadResume(e.target.files[0]); },
    resetUpload() { this.uploadedFile = null; this.parsedData = null; this.isParsing = false; },
    triggerErrorToast(msg) { this.errorMessage = msg; setTimeout(() => { this.errorMessage = ''; }, 7000); },

    async uploadResume(file) {
      const user = this.getLocalUser();
      if (!user || !user.id || !user.email) { this.triggerErrorToast("Error: Missing session."); return; }

      this.uploadedFile = file; 
      this.uploadedFileName = file.name; 
      this.isParsing = true;
      this.startParsingAnimation(); 

      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('user_id', user.id);
        formData.append('expected_email', user.email); 

        const response = await axios.post(`${process.env.VUE_APP_API_URL}/sync-resume/`, formData, {
          headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${this.getApiToken()}` }
        });
        
        const parsedEmail = response.data.email || response.data.parsed_email;
        if (parsedEmail && parsedEmail.toLowerCase() !== user.email.toLowerCase()) {
            this.isParsing = false;
            this.triggerErrorToast(this.$t('orgDashboard.userDashboard.careerSimulator.secBlockError', { email: parsedEmail, userEmail: user.email }));
            this.resetUpload(); return;
        }
        
        this.formData.supportingSkills = [];
        this.formData.alternateSkills = [];
        this.formData.targetAreaBase = "";
        this.formData.industryBase = "";
        this.internalData = { careerPaths: null, openRoles: [] };
        this.externalData = { careerPaths: null, openRoles: [] };
        this.needsRefetch = true;
        this.parsingStage = 2;
        
        setTimeout(async () => {
           this.isParsing = false;
           await this.forceFetchUserData();
        }, 600);

      } catch (error) {
        this.isParsing = false;
        if (error.response && (error.response.status === 403 || error.response.status === 400)) {
            this.triggerErrorToast(error.response.data.detail || this.$t('orgDashboard.userDashboard.careerSimulator.secBlockError'));
        } else { 
            this.triggerErrorToast(this.$t('orgDashboard.userDashboard.careerSimulator.failedParse')); 
        }
        this.resetUpload();
      }
    },

    async generatePredictions() {
      if (!this.needsRefetch && this.internalData.careerPaths && this.externalData.careerPaths) {
        this.currentStep = 6;
        this.updateActiveView();
        return;
      }

      this.isPredicting = true;
      try {
        let orgId = "default_org";
        try {
          const orgData = JSON.parse(localStorage.getItem('preferred_org_selection'));
          if (orgData && orgData.id) orgId = orgData.id;
        } catch(e) {}
        
        const user = this.getLocalUser();
        const payload = {
          email: user.email, 
          user_id: user.id, 
          organization_id: orgId,
          target_area: this.finalTargetArea || "General", 
          industry: this.finalIndustry || "General",
          current_skills: this.formData.supportingSkills,
          alternate_skills: this.formData.alternateSkills,
          selected_supporting_skill: this.formData.supportingSkills.join(", "),
          selected_alternate_skill: this.formData.alternateSkills.join(", ")
        };

        const headers = { Authorization: `Bearer ${this.getApiToken()}` };

        const [internalRes, externalRes] = await Promise.all([
          axios.post(`${process.env.VUE_APP_API_URL}/api/career-simulator/predict`, payload, { headers }),
          axios.post(`${process.env.VUE_APP_API_URL}/api/career-simulator/predict-external`, payload, { headers })
        ]);

        if (internalRes.data) {
          this.internalData.careerPaths = internalRes.data.career_paths;
          this.internalData.openRoles = internalRes.data.matched_open_roles || [];
        }

        if (externalRes.data) {
          this.externalData.careerPaths = externalRes.data.career_paths;
          this.externalData.openRoles = externalRes.data.market_opportunities || [];
          if (externalRes.data.career_paths.readyLater && externalRes.data.career_paths.readyLater.length > 0) {
              this.ultimateFutureRole = externalRes.data.career_paths.readyLater[0].title;
          }
        }

        this.needsRefetch = false;
        this.updateActiveView();
        
        if (this.currentStep < 6) this.currentStep = 6;

      } catch (error) {
        console.error("Parallel Prediction Error:", error);
        this.triggerErrorToast(this.$t('orgDashboard.userDashboard.careerSimulator.failedPredict'));
      } finally {
        this.isPredicting = false;
      }
    },

    updateActiveView() {
      const source = this.opportunityScope === 'org' ? this.internalData : this.externalData;
      
      if (source && source.careerPaths) {
        // 1. Force instant reactivity inside Vue 2 using deep copy
        this.careerPaths = JSON.parse(JSON.stringify(source.careerPaths));
        
        let syncedRoles = source.openRoles ? JSON.parse(JSON.stringify(source.openRoles)) : [];
        
        const getFitForPath = (pathTitle) => {
          if (!pathTitle) return null;
          if (this.careerPaths.readyNow) {
              const idx = this.careerPaths.readyNow.findIndex(p => p.title === pathTitle);
              if (idx > -1) return 89 - (idx * 5);
          }
          if (this.careerPaths.readyShortly) {
              const idx = this.careerPaths.readyShortly.findIndex(p => p.title === pathTitle);
              if (idx > -1) return 63 - (idx * 5);
          }
          if (this.careerPaths.readyLater) {
              const idx = this.careerPaths.readyLater.findIndex(p => p.title === pathTitle);
              if (idx > -1) return 18 - (idx * 5);
          }
          return null;
        };

        syncedRoles.forEach(role => {
          const step6Fit = getFitForPath(role.mapped_from_path);
          if (step6Fit !== null) {
            role.match_percent = step6Fit;
          }
        });

        this.openRoles = syncedRoles;
        this.generateChartData();
        
        // 2. Performance optimization: Bypasses drawing calculations 
        // entirely when the user is on the Time Readiness tab.
        if (this.readinessTab === 'skill') {
          this.$nextTick(() => {
            // Guarantee elements have recalculated dimensions after data changes
            setTimeout(this.drawLines, 150);
          });
        }
      } else {
        if (this.currentStep === 6) {
          this.generatePredictions();
        }
      }
    },

    async finishWizard() {
      // Step 7 "Finish" button now goes to Step 8 instead of immediately redirecting.
      if (this.currentStep === 7) {
        this.currentStep = 8;
        return;
      }
      
      const user = this.getLocalUser();
      try {
        const payload = {
          user_id: user.id, email: user.email, target_area: this.finalTargetArea || "General",
          industry: this.finalIndustry || "General", current_skills: this.formData.supportingSkills,
          alternate_skills: this.formData.alternateSkills, career_paths: this.careerPaths,
          matched_open_roles: this.openRoles
        };
        await axios.post(`${process.env.VUE_APP_API_URL}/api/career-simulator/save`, payload, { headers: { Authorization: `Bearer ${this.getApiToken()}` } });
        await axios.delete(`${process.env.VUE_APP_API_URL}/api/career-simulator/session?user_id=${user.id}`, { headers: { Authorization: `Bearer ${this.getApiToken()}` } });
      } catch (error) { 
        console.error("Failed to save metrics", error); 
      }
      window.location.href = '/user/dashboard'; 
    },

    generateAndDownloadBlueprint() {
      // 1. Turn on the spinner IMMEDIATELY
      this.isGeneratingBlueprint = true;

      // 2. Force the browser to wait 200ms and paint the spinner to the screen
      // BEFORE it does any heavy lifting, payload building, or API calls.
      setTimeout(async () => {
        try {
          const user = this.getLocalUser();
          
          // Extract their ACTUAL resume history safely
          let resumeContext = "No prior experience provided.";
          if (this.parsedData && this.parsedData.rawTimeline && this.parsedData.rawTimeline.length > 0) {
             resumeContext = this.parsedData.rawTimeline.map(r => `${r.title} at ${r.company}`).join(" -> ");
          } else if (this.parsedData && this.parsedData.skills && this.parsedData.skills.length > 0) {
             resumeContext = "Background primarily in: " + this.parsedData.skills.slice(0, 8).map(s => s.name).join(", ");
          }

          const payload = {
            user_id: user.id,
            email: user.email,
            target_area: this.finalTargetArea || "General",
            industry: this.finalIndustry || "General",
            current_skills: this.formData.supportingSkills,
            alternate_skills: this.formData.alternateSkills,
            target_role: this.ultimateFutureRole || "Target Leadership Role",
            chart_image: this.capturedChartImage || "",
            resume_summary: resumeContext
          };

          const response = await axios.post(`${process.env.VUE_APP_API_URL}/api/career-simulator/generate-blueprint`, payload, {
            headers: { Authorization: `Bearer ${this.getApiToken()}` },
            responseType: 'blob' 
          });

          // Trigger the download
          const fileUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
          const link = document.createElement('a');
          link.href = fileUrl;
          link.setAttribute('download', `Career_Blueprint_${payload.target_role.replace(/\s+/g, '_')}.pdf`);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          
        } catch (error) {
          console.error("Failed to generate blueprint:", error);
          this.triggerErrorToast("Failed to generate the blueprint. Please try again.");
        } finally {
          // 3. Turn off the spinner only when everything is 100% finished
          this.isGeneratingBlueprint = false;
        }
      }, 200); // 200ms delay guarantees the UI updates visually first
    },

    generateChartData() {
      const allRoles = [];
      const processCategory = (arr, fitBase) => {
        if(!arr) return;
        arr.forEach((role, i) => {
          let missingSkills = [];
          if (role.gaps && role.gaps.includes(":")) { missingSkills = role.gaps.split(":")[1].split(",").map(s => s.trim()); }
          
          // Use ALL supporting skills selected, not just the first 3
          const haveSkills = [...this.formData.supportingSkills]; 
          
          allRoles.push({
            title: role.title, time: role.time, fit: fitBase - (i * 5),
            skillsHave: haveSkills, skillsMissing: missingSkills
          });
        });
      };
      processCategory(this.careerPaths.readyNow, 89);
      processCategory(this.careerPaths.readyShortly, 63);
      processCategory(this.careerPaths.readyLater, 18);
      this.allChartRoles = allRoles;

      const skillMap = new Map();
      allRoles.forEach(r => {
        r.skillsHave.forEach(s => skillMap.set(s, 'have'));
        r.skillsMissing.forEach(s => skillMap.set(s, 'gap'));
      });
      this.allChartSkills = Array.from(skillMap.keys()).map(k => ({ name: k, status: skillMap.get(k) }));
    },
    
    isSkillAssociated(skillName, roleIndex) {
      if (roleIndex === null) return true;
      const role = this.allChartRoles[roleIndex];
      if (!role) return false;
      return role.skillsHave.includes(skillName) || role.skillsMissing.includes(skillName);
    },

    openRoleDetails(role, event) { 
      this.selectedRoleDetails = role; 
      const rect = event.currentTarget.getBoundingClientRect();
      const wrapperRect = this.$refs.chartWrapper.getBoundingClientRect();
      
      let calculatedLeft = (rect.right - wrapperRect.left) + 20;
      let calculatedTop = (rect.top - wrapperRect.top) - 20;

      if (calculatedLeft + 420 > wrapperRect.width) {
         calculatedLeft = (rect.left - wrapperRect.left) - 440;
      }
      this.popupStyle = { top: `${calculatedTop}px`, left: `${calculatedLeft}px`, position: 'absolute' };
    },

    openKanbanRoleDetails(path, timeGroup, event) {
      let missingSkills = [];
      if (path.gaps && path.gaps.includes(":")) {
         missingSkills = path.gaps.split(":")[1].split(",").map(s => s.trim());
      } else if (path.gaps && path.gaps.toLowerCase() !== 'none') {
         missingSkills = [path.gaps];
      }

      let fit = timeGroup === 'readyNow' ? 89 : timeGroup === 'readyShortly' ? 65 : 25;

      this.selectedRoleDetails = {
        title: path.title,
        time: path.time || (timeGroup === 'readyNow' ? '~0mo' : timeGroup === 'readyShortly' ? '~4mo' : '~9mo'),
        fit: fit,
        skillsHave: this.formData.supportingSkills.slice(0, 4),
        skillsMissing: missingSkills
      };

      let top = event.clientY + 20;
      let left = event.clientX + 20;

      if (left + 420 > window.innerWidth) {
          left = event.clientX - 440;
      }
      if (top + 300 > window.innerHeight) {
          top = event.clientY - 320;
      }

      this.popupStyle = {
        top: `${top}px`,
        left: `${left}px`,
        position: 'fixed',
        zIndex: 99999
      };
    },
    
    closePopup() { this.selectedRoleDetails = null; },

    drawLines() {
    if (this.currentStep !== 6 || this.readinessTab !== 'skill') return; 
    const wrapper = this.$refs.chartWrapper; 
    const rootNode = this.$refs.rootNode;
    if (!wrapper || !rootNode) return;

    const wrapperRect = wrapper.getBoundingClientRect();
    
    const getCenter = (el, isRight) => {
      if (!el) return { x: 0, y: 0 };
      const rect = el.getBoundingClientRect();
      if (rect.width === 0 && rect.height === 0) return { x: 0, y: 0 }; 
      return { 
         x: (isRight ? rect.right : rect.left) - wrapperRect.left, 
         y: (rect.top + (rect.height / 2)) - wrapperRect.top 
      };
    };
    
    const generateBezier = (p1, p2) => {
      if (p1.x === 0 && p1.y === 0) return '';
      const distance = Math.abs(p2.x - p1.x); 
      const controlOffset = distance * 0.4;
      return `M ${p1.x} ${p1.y} C ${p1.x + controlOffset} ${p1.y}, ${p2.x - controlOffset} ${p2.y}, ${p2.x} ${p2.y}`;
    };

    const rootPos = getCenter(rootNode, true);
    const newLinks = { rootToRoles: [], rolesToSkills: [] };

    this.allChartRoles.forEach((role, rIndex) => {
      const roleRefArray = this.$refs['roleNode-' + rIndex];
      if (!roleRefArray) return;
      const rEl = Array.isArray(roleRefArray) ? roleRefArray[0] : roleRefArray;
      if (!rEl) return;

      const rLeftPos = getCenter(rEl, false);
      newLinks.rootToRoles.push({ id: 'rt-' + rIndex, roleId: rIndex, path: generateBezier(rootPos, rLeftPos) });

      const rRightPos = getCenter(rEl, true);
      this.allChartSkills.forEach((skill, sIndex) => {
        if (role.skillsHave.includes(skill.name) || role.skillsMissing.includes(skill.name)) {
          const skillRefArray = this.$refs['skillNode-' + sIndex];
          if (!skillRefArray) return;
          const sEl = Array.isArray(skillRefArray) ? skillRefArray[0] : skillRefArray;
          if (!sEl) return;

          const sLeftPos = getCenter(sEl, false);
          const status = role.skillsHave.includes(skill.name) ? 'have' : 'gap';
          
          let lineThickness = 2.5; 
          const userSkillData = this.parsedData?.skills?.find(s => s.name === skill.name);
          if (userSkillData && userSkillData.level) {
              const lvl = String(userSkillData.level).toLowerCase();
              if (lvl === 'beginner' || lvl === 'basic') lineThickness = 1.5;
              else if (lvl === 'intermediate') lineThickness = 3.5;
              else if (lvl === 'advanced' || lvl === 'expert') lineThickness = 6.0;
          }

          newLinks.rolesToSkills.push({ 
              id: `rs-${rIndex}-${sIndex}`, 
              roleId: rIndex, 
              status: status, 
              path: generateBezier(rRightPos, sLeftPos),
              width: lineThickness
          });
        }
      });
    });
    this.chartLinks = newLinks;

    // DECOUPLED & OPTIMIZED SCREENSHOT CAPTURE ENGINE
    if (this.captureTimeout) clearTimeout(this.captureTimeout);
    this.captureTimeout = setTimeout(() => {
      const chartEl = this.$refs.chartWrapper;
      if (chartEl && this.currentStep === 6 && this.readinessTab === 'skill') {
        
        const runCapture = async () => {
          try {
            // scale reduced to 1.3 to avoid thread locking while keeping visual quality clean
            const canvas = await html2canvas(chartEl, { 
              scale: 1.3, 
              useCORS: true, 
              backgroundColor: "#FFFFFF",
              logging: false 
            });
            this.capturedChartImage = canvas.toDataURL("image/png");
            this.triggerDbSave();
          } catch (e) {
            console.warn("Bypassed snapshot generation", e);
          }
        };

        // Offload execution to requestIdleCallback if supported to let tabs transition cleanly first
        if (window.requestIdleCallback) {
          window.requestIdleCallback(() => runCapture(), { timeout: 3500 });
        } else {
          requestAnimationFrame(() => runCapture());
        }
      }
    }, 2500); // 2.5s delay ensures transitions and reflows are complete before capturing
  }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800&display=swap');

:root { 
  --primary-color: #FBB603; 
  --primary-hover: #E5A000; 
  --bg-color: #FAFAFA; 
}

/* --- OVERALL LAYOUT / STRICT SCROLL CONTROL --- */
.career-simulator-container { 
  display: flex; 
  flex-direction: column; 
  min-height: calc(100vh - 60px); 
  background-color: #FAFAFA !important; 
  font-family: 'Poppins', -apple-system, BlinkMacSystemFont, sans-serif !important; 
  color: #111827 !important; 
  position: relative;
}

.wizard-content { 
  flex: 1; 
  width: 100%; 
  max-width: 1400px;
  margin: 0 auto; /* <--- FIX: Added 'auto' to center on zoom-out */
  padding: 2rem 3rem 120px 3rem; 
  display: flex;
  flex-direction: column;
}

.wizard-content.no-scroll {
  overflow: visible !important;
}

/* ========================================================
   NEW BOX LAYOUT ("Wallet" style UI) 
   ======================================================== */
.step-layout {
  width: 100%; 
  max-width: 100%; 
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1rem; 
}

.custom-select-wrapper, 
.custom-skill-input {
  max-width: 800px; 
}

.chart-wrapper { 
  position: relative; 
  width: 100%; 
  min-height: 520px; 
  overflow: visible; 
  padding: 2rem;
}

.roles-grid { 
  display: grid; 
  grid-template-columns: repeat(3, 1fr); 
  gap: 1.5rem; 
  width: 100%;
}

@media (max-width: 1200px) {
  .roles-grid { grid-template-columns: repeat(2, 1fr); }
}

.step-header-box {
  width: 100%;
  text-align: left; 
  margin-bottom: 0.5rem;
}

.step-content-card {
  background: #FFFFFF;
  border: 1px solid #E5E7EB;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 2px 4px rgba(0,0,0,0.02);
  width: 100%;
  text-align: left;
}
.page-title { font-size: 1.7rem; font-weight: 700; color: #111827; margin: 0 0 0.5rem 0; letter-spacing: -0.02em; }
.page-subtitle { font-size: 0.95rem; color: #4B5563; font-weight: 400; margin: 0; line-height: 1.5; }
.card-inner-title { font-size: 1.05rem; font-weight: 700; color: #111827; margin-bottom: 0.25rem; }
.card-inner-subtitle { font-size: 0.85rem; color: #6B7280; margin-bottom: 1.5rem; line-height: 1.5;}

/* --- STEP 1: CV SPLIT --- */
.cv-split-view { 
  display: flex; 
  gap: 3rem; 
  align-items: stretch; /* CRITICAL: Forces both columns to be the exact same height */
  justify-content: center; 
  width: 100%; 
  max-width: 1100px; 
  margin: 0 auto; 
} 
.cv-split-view > * { 
  flex: none !important; 
}
.upload-box { 
  width: 380px; 
  border: 2px dashed #D1D5DB; 
  border-radius: 12px; 
  padding: 2rem; 
  text-align: center; 
  background: white; 
  transition: all 0.3s ease; 
  display: flex; 
  flex-direction: column; 
  justify-content: center; 
  min-height: 560px; /* Fixed Height */
}
.upload-box.is-success {
  padding: 0 !important; 
  border: none !important;
  background: transparent !important;
}
.upload-box.is-dragover { border-color: #FBB603; background: #FFFBEB; transform: scale(1.02);}
.upload-icon { font-size: 2rem; color: #FBB603; margin-bottom: 0.5rem; }
.success-icon { font-size: 2.5rem; color: #10B981; margin-bottom: 0.5rem; }
.upload-box h3 { font-size: 1.2rem; font-weight: 700; color: #111827; margin-bottom: 0.5rem;}
.parsed-preview-box { 
  width: 650px; 
  background: white; 
  border-radius: 12px; 
  padding: 2.5rem 2rem; 
  box-shadow: 0 2px 4px rgba(0,0,0,0.02); 
  border: 1px solid #E5E7EB; 
  height: 100%; /* Stretches to match container */
  min-height: 560px; /* Fixed Height */
  display: flex;
  flex-direction: column;
}
.preview-header { display: flex; justify-content: space-between; align-items: flex-start; padding-bottom: 1rem; border-bottom: 1px solid #F3F4F6;}
.profile-info { display: flex; align-items: center; gap: 1rem;}
.avatar-circle { width: 48px; height: 48px; border-radius: 50%; background: #FBB603; color: white; display: flex; align-items: center; justify-content: center; font-weight: bold; font-size: 1rem; border: 3px solid #FFF; box-shadow: 0 2px 8px rgba(0,0,0,0.1);}
.eyebrow { font-size: 0.7rem; text-transform: uppercase; color: #9CA3AF; letter-spacing: 0.05em; font-weight: 700;}
.preview-header h4 { margin: 0; font-size: 1.1rem; color: #111827 !important; font-weight: 700;}
.preview-header p { margin:0; font-size: 0.8rem; color: #6B7280; }
.velocity-badge { background: #ECFDF5; color: #059669; padding: 0.3rem 0.8rem; border-radius: 999px; font-size: 0.75rem; font-weight: 600; border: 1px solid #A7F3D0;}
.preview-section h5 { font-size: 0.75rem; color: #9CA3AF !important; margin-bottom: 0.8rem; font-weight: 700; text-transform: uppercase;}
.tags-container { display: flex; flex-wrap: wrap; gap: 0.6rem; }
.skill-tag { background: #F9FAFB; padding: 0.4rem 0.8rem; border-radius: 8px; font-size: 0.8rem; display: flex; align-items: center; gap: 0.5rem; border: 1px solid #E5E7EB; color: #374151 !important; font-weight: 500; }
.skill-tag .percent { color: #F59E0B; font-weight: 700; font-size: 0.7rem; }
.overflow-tag { background: #F3F4F6; color: #6B7280 !important; font-style: italic;}
.role-bars { display: flex; flex-direction: column; gap: 0.8rem;}
.role-bar-item { display: flex; align-items: center; gap: 1rem; font-size: 0.85rem; font-weight: 500; color: #1F2937 !important;}
.role-title { width: 140px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.bar-wrap { flex: 1; height: 8px; background: #F3F4F6; border-radius: 4px; overflow: hidden; }
.bar-fill { height: 100%; background: linear-gradient(90deg, #FBB603, #F59E0B); border-radius: 4px; }
.match-text { width: 40px; text-align: right; color: #6B7280; font-weight: 700; font-size: 0.85rem;}

/* --- INPUTS & SELECTIONS --- */
.custom-select-wrapper { width: 100%; position: relative;}
.custom-skill-input, .custom-select-display {
  width: 100%; padding: 0.9rem 1.25rem; font-size: 0.95rem; 
  border: 1px solid #D1D5DB; border-radius: 8px; 
  background: white; cursor: pointer; color: #111827; 
  font-family: 'Poppins', sans-serif; font-weight: 500;
  display: flex; justify-content: space-between; align-items: center;
  transition: all 0.2s; outline: none;
}
.custom-select-display:hover, .custom-skill-input:focus { border-color: #9CA3AF; }
.custom-select-display.is-open { border-color: #9CA3AF; }
.dropdown-arrow { font-size: 0.8rem; color: #9CA3AF; transition: transform 0.2s; }
.custom-select-display.is-open .dropdown-arrow { transform: rotate(180deg); }
.custom-dropdown-list { position: absolute; top: 110%; left: 0; width: 100%; background: white; border: 1px solid #E5E7EB; border-radius: 8px; box-shadow: 0 4px 15px rgba(0,0,0,0.1); z-index: 50; overflow: hidden; }
.custom-dropdown-option { padding: 0.8rem 1.25rem; cursor: pointer; font-size: 0.95rem; color: #374151; font-weight: 500; border-bottom: 1px solid #F3F4F6; transition: all 0.1s ease; }
.custom-dropdown-option:last-child { border-bottom: none; }
.custom-dropdown-option:hover { background-color: #F9FAFB; color: #111827; }

/* Selected Summary Box (Steps 2,3,4,5) */
.selected-box { background-color: #F9FAFB; border-radius: 8px; padding: 1.25rem; margin-top: 1.5rem; }
.selected-box-title { font-size: 0.9rem; font-weight: 700; color: #111827; margin-bottom: 0.25rem; }
.selected-box-desc { font-size: 0.85rem; color: #6B7280; }

/* Pills (Steps 4 & 5) */
.skill-pills-container { display: flex; flex-wrap: wrap; gap: 0.75rem; margin-bottom: 1.5rem; }
.v0-pill { padding: 0.5rem 1.2rem; border-radius: 999px; font-size: 0.9rem; font-weight: 500; background: white; border: 1px solid #D1D5DB; color: #374151; cursor: pointer; transition: all 0.2s; }
.v0-pill:hover { border-color: #9CA3AF; }
.v0-pill.active { background: #FBB603; border-color: #FBB603; color: #111827; font-weight: 600; box-shadow: 0 2px 4px rgba(251, 182, 3, 0.2);}

/* Removable Chips */
.selected-chips { display: flex; flex-wrap: wrap; gap: 0.5rem; margin-top: 0.5rem; }
.v0-chip { background: white; border: 1px solid #E5E7EB; border-radius: 6px; padding: 0.35rem 0.75rem; font-size: 0.85rem; color: #374151; display: inline-flex; align-items: center; gap: 0.5rem; font-weight: 500;}
.v0-chip .remove { cursor: pointer; color: #9CA3AF; font-weight: bold; font-size: 1rem; line-height: 1;}
.v0-chip .remove:hover { color: #EF4444; }

/* --- CHART (STEP 6) --- */
.chart-headers { display: flex; justify-content: space-between; margin-bottom: 2rem; padding: 0 1rem; border-bottom: 1px solid #F3F4F6; padding-bottom: 1rem;}
.chart-headers span { font-size: 0.75rem; font-weight: 700; color: #9CA3AF; text-transform: uppercase; letter-spacing: 0.05em;}
.chart-svg-layer { position: absolute; top: 0; left: 0; right: 0; bottom: 0; width: 100%; height: 100%; pointer-events: none; z-index: 1; }
.chart-line { fill: none; transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1); stroke: #E5E7EB; }
.chart-line.root-line { stroke: #FDE68A; opacity: 0.8; stroke-width: 2.5px;}
.chart-line.have { stroke: #34D399; opacity: 0.7;} 
.chart-line.gap { stroke: #FCA5A5; opacity: 0.7;}
.chart-line.dimmed { opacity: 0.05 !important; stroke-width: 1px !important;} 
.chart-line.highlighted { opacity: 1 !important; filter: drop-shadow(0 2px 4px rgba(0,0,0,0.1));}
.chart-line.highlighted.have { stroke: #10B981; }
.chart-line.highlighted.gap { stroke: #EF4444; }

.chart-nodes-layer { position: relative; z-index: 2; display: flex; justify-content: space-between; min-height: 380px; }
.chart-col { display: flex; flex-direction: column; justify-content: space-around; gap: 1rem; z-index: 5;}
.col-root { align-items: flex-start; width: 25%;} 
.col-roles { align-items: center; width: 40%;} 
.col-skills { align-items: flex-end; width: 30%;}

.chart-node { display: flex; align-items: center; gap: 0.8rem; padding: 0.5rem 0; transition: all 0.3s; cursor: pointer;}
.node-root { cursor: default; }
.node-label { font-size: 0.9rem; font-weight: 500; color: #4B5563; background: rgba(255,255,255,0.9); padding: 4px 8px; border-radius: 6px;}
.node-role:hover .node-label.title { color: #D97706; font-weight: 600; }
.node-dot { width: 14px; height: 14px; border-radius: 50%; background: white; z-index: 10;}
.shadow-dot { box-shadow: 0 0 0 4px rgba(255,255,255,0.8), 0 2px 4px rgba(0,0,0,0.1); }
.bg-orange { background: #F59E0B; } .bg-green { background: #10B981; } .bg-yellow { background: #FBB603; }
.node-dot.border-orange { border: 3px solid #F59E0B !important; } 
.node-dot.border-green { border: 3px solid #10B981 !important; }

.role-text-wrap { text-align: center; margin: 0 0.5rem; background: white; padding: 0.6rem 1rem; border-radius: 8px; border: 1px solid #E5E7EB; box-shadow: 0 2px 4px rgba(0,0,0,0.02); transition: all 0.3s ease;}
.node-role:hover .role-text-wrap { border-color: #FBB603; box-shadow: 0 4px 8px rgba(251, 182, 3, 0.1); transform: translateY(-2px);}
.node-meta { font-size: 0.75rem; color: #9CA3AF; display: block; margin-top: 4px; font-weight: 500;}

.chart-legend { display: flex; flex-direction: row; justify-content: space-between; align-items: center; width: 100%; margin-top: 1rem; border-top: 1px solid #E5E7EB; padding-top: 1rem; }
.legend-left { display: flex; flex-direction: row; gap: 1rem; align-items: center; }
.legend-item { display: flex; flex-direction: row; align-items: center; gap: 0.4rem; font-size: 0.8rem; color: #4B5563; font-weight: 500;}
.legend-hint { font-size: 0.8rem; color: #9CA3AF; font-style: italic; }

.history-trigger-dot { width: 28px; height: 28px; border-radius: 50%; background: #FFFBEB; border: 2px solid #FBB603; display: flex; align-items: center; justify-content: center; cursor: pointer; transition: all 0.2s ease; box-shadow: 0 2px 4px rgba(251, 182, 3, 0.1); }
.history-trigger-dot:hover { background: #FBB603; transform: scale(1.1); box-shadow: 0 4px 8px rgba(251, 182, 3, 0.2); }
.dot-icon { font-size: 14px; transition: color 0.2s;}
.history-trigger-dot:hover .dot-icon { color: white; }

/* Popups */
.custom-tooltip-parent { position: relative; }
.tooltip-text { visibility: hidden; width: max-content; background-color: #1F2937; color: #fff; text-align: center; border-radius: 6px; padding: 6px 10px; position: absolute; z-index: 100; bottom: 120%; left: 50%; transform: translateX(-50%); font-size: 0.75rem; opacity: 0; transition: opacity 0.2s; font-weight: 600; }
.tooltip-text.left-tooltip { left: auto; right: 130%; bottom: auto; top: 50%; transform: translateY(-50%); }
.tooltip-text.left-tooltip::after { content: " "; position: absolute; top: 50%; left: 100%; margin-top: -5px; border-width: 5px; border-style: solid; border-color: transparent transparent transparent #1F2937; }
.custom-tooltip-parent:hover .tooltip-text { visibility: visible; opacity: 1; }

.role-detail-popup.floating-popup { background: white; border-radius: 12px; width: 400px; padding: 1.5rem; box-shadow: 0 10px 25px rgba(0,0,0,0.1); border: 1px solid #E5E7EB; border-top: 4px solid #FBB603; z-index: 1000; }
.popup-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.5rem;}
.dept { font-size: 0.7rem; font-weight: 700; color: #9CA3AF; text-transform: uppercase; letter-spacing: 0.05em;}
.close-btn { background: transparent; border: none; font-size: 1.4rem; color: #9CA3AF; cursor: pointer; transition: color 0.2s; line-height: 1;}
.close-btn:hover { color: #111827; }
.popup-title-row { display: flex; justify-content: space-between; align-items: center;}
.popup-title-row h3 { margin: 0; font-size: 1.2rem; color: #111827; font-weight: 700;}
.term-badge { background: #FFFBEB; color: #D97706; padding: 0.2rem 0.8rem; border-radius: 999px; font-size: 0.75rem; font-weight: 600; border: 1px solid #FDE68A;}

.fit-section { display: flex; justify-content: space-between; font-size: 0.85rem; font-weight: 600; color: #374151; margin-bottom: 0.4rem;}
.match-bar-wrap { height: 6px; background: #F3F4F6; border-radius: 3px; overflow: hidden;}
.match-bar { height: 100%; background: #FBB603; border-radius: 3px;}
.skills-split { display: flex; gap: 1rem; }
.skills-split > div { flex: 1; }
.skills-block h6 { font-size: 0.75rem; color: #9CA3AF; margin-bottom: 0.6rem; letter-spacing: 0.05em; font-weight: 700;}
.skill-chips { display: flex; flex-wrap: wrap; gap: 0.4rem; }
.skill-chips span { padding: 0.3rem 0.6rem; border-radius: 6px; font-size: 0.75rem; font-weight: 500;}
.skill-chips.has span { background: #ECFDF5; color: #059669; border: 1px solid #A7F3D0;}
.skill-chips.missing span { background: #FEF2F2; color: #DC2626; border: 1px solid #FECACA;}
.empty-chip { background: transparent !important; border: 1px dashed #D1D5DB !important; color: #9CA3AF !important;}


/* ========================================================
   NEW: TIME KANBAN STYLES (Match design perfectly)
   ======================================================== */
.kanban-board-modern { 
  display: grid; 
  grid-template-columns: repeat(3, 1fr); 
  gap: 1.5rem; 
  width: 100%; 
}

.kanban-col-modern { 
  background: #FFFFFF; 
  border-radius: 12px; 
  padding: 1.5rem; 
  display: flex; 
  flex-direction: column; 
  border: 1px solid #E5E7EB; /* Base border */
}

/* Specific colored borders completely outlining the columns */
.kanban-col-modern.border-green  { border: 1.5px solid #10B981; }
.kanban-col-modern.border-yellow { border: 1.5px solid #FBB603; }
.kanban-col-modern.border-orange { border: 1.5px solid #F97316; }

.col-header-modern { margin-bottom: 1.5rem; }

.col-header-modern .time-badge { 
  font-size: 0.75rem; 
  font-weight: 700; 
  letter-spacing: 0.05em; 
  text-transform: uppercase; 
  display: block; 
  margin-bottom: 0.4rem;
}
.text-green-600 { color: #059669; } 
.text-yellow-600 { color: #D97706; } 
.text-orange-600 { color: #EA580C; }

.col-header-modern h3 { 
  font-size: 1.25rem; 
  font-weight: 700; 
  color: #111827; 
  margin: 0; 
}

.role-card-modern { 
  background: #FFFFFF; 
  border: 1px solid #F3F4F6; 
  border-radius: 8px; 
  padding: 1.25rem; 
  margin-bottom: 1rem; 
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1); 
  cursor: pointer; 
  box-shadow: 0 1px 3px rgba(0,0,0,0.02);
  position: relative;
  overflow: hidden;
}

/* Subtle accent border inside the card on hover */
.role-card-modern::before {
  content: ''; position: absolute; left: 0; top: 0; bottom: 0; width: 4px;
  background: transparent; transition: background 0.3s ease;
}
.kanban-col-modern.border-green .role-card-modern:hover::before { background: #10B981; }
.kanban-col-modern.border-yellow .role-card-modern:hover::before { background: #FBB603; }
.kanban-col-modern.border-orange .role-card-modern:hover::before { background: #F97316; }

/* Interactive hovering */
.role-card-modern:hover { 
  transform: translateY(-4px); 
  box-shadow: 0 12px 20px -8px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05); 
  border-color: #E5E7EB;
}

.role-card-modern h4 { 
  margin: 0 0 0.5rem 0; 
  font-size: 1rem; 
  color: #111827; 
  font-weight: 700;
}
.close-gaps-modern { 
  font-size: 0.8rem; 
  color: #6B7280; 
  margin: 0 0 1rem 0; 
  line-height: 1.5; 
}
.time-est-modern { 
  font-size: 0.85rem; 
  color: #4B5563; 
  font-weight: 600; 
  display: inline-block;
}

.col-body-modern.empty-state { 
  flex: 1; 
  display: flex; 
  align-items: center; 
  justify-content: center; 
  color: #9CA3AF; 
  font-size: 0.9rem; 
  font-style: italic; 
  padding: 2rem 0;
}


/* --- ROLES GRID (STEP 7) --- */
.roles-grid { 
  display: grid; 
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr)); /* slightly optimized for 3 columns */
  gap: 1.5rem; 
  width: 100%;
}
.open-role-card { 
  background: white; 
  border-radius: 12px; 
  padding: 1.5rem; 
  box-shadow: 0 2px 4px rgba(0,0,0,0.02); 
  border: 1px solid #E5E7EB; 
  transition: all 0.3s ease;
  
  /* NEW: Flexbox setup to push button to the bottom */
  display: flex;
  flex-direction: column;
  height: 100%; 
}
.open-role-card:hover { 
  border-color: #FDE68A; 
  box-shadow: 0 10px 20px rgba(0,0,0,0.04);
}
.action-area {
  margin-top: auto !important; 
  border-top: 1px solid #F3F4F6;
  padding-top: 1rem; /* Adjust padding to look balanced */
  text-align: right;
}
.card-head { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 1rem; }
.card-head h3 { margin: 0.3rem 0; font-size: 1.2rem; color: #111827; font-weight: 700;}
.match-badge { padding: 0.4rem 0.8rem; border-radius: 999px; font-size: 0.85rem; font-weight: 600; }
.match-badge.high { background: #FFFBEB; color: #D97706; border: 1px solid #FDE68A;}
.match-badge.medium { background: #F3F4F6; color: #4B5563; }
.mapped-text { font-size: 0.9rem; color: #4B5563; margin-bottom: 1rem;}


/* --- STICKY FOOTER NAV --- */
.wizard-footer { 
  position: sticky; 
  bottom: 0; 
  width: 100%; 
  background: #FAFAFA; 
  border-top: 1px solid #E5E7EB; 
  z-index: 9999; 
  padding: 1rem 0; 
  box-shadow: 0 -10px 20px rgba(250, 250, 250, 0.95); 
}

.footer-inner {
  width: 100%;
  max-width: 1400px; 
  margin: 0 auto; /* <--- FIX: Added 'auto' to center on zoom-out */
  padding: 0 3rem; 
  display: flex; 
  justify-content: space-between; 
  align-items: center; 
}

.footer-left { 
  flex: 1; 
  display: flex; 
  justify-content: flex-start; 
}

.footer-stepper {
  flex: 0 0 auto;
  display: flex; 
  align-items: center; 
  justify-content: center; 
  gap: 1rem;
}

.footer-right { 
  flex: 1; 
  display: flex; 
  justify-content: flex-end; 
}

.stepper-icon-item { position: relative; cursor: pointer; display: flex; align-items: center; justify-content: center; }
.stepper-dot-icon { width: 36px; height: 36px; border-radius: 50%; background: white; display: flex; align-items: center; justify-content: center; font-size: 1rem; color: #9CA3AF; transition: all 0.2s ease; border: 1px solid #D1D5DB; }
.stepper-icon-item.active .stepper-dot-icon { background: #FBB603; border-color: #FBB603; color: balck; transform: scale(1.1); box-shadow: 0 4px 10px rgba(251, 182, 3, 0.2); }
.stepper-icon-item.completed .stepper-dot-icon { background: #10B981; border-color: #10B981; color: white; }
.stepper-tooltip { position: absolute; bottom: 140%; left: 50%; transform: translateX(-50%); background: #1F2937; color: white; padding: 6px 12px; border-radius: 6px; font-size: 0.75rem; font-weight: 600; white-space: nowrap; opacity: 0; visibility: hidden; transition: all 0.2s ease; pointer-events: none; z-index: 10; }
.stepper-tooltip::after { content: ''; position: absolute; top: 100%; left: 50%; margin-left: -5px; border-width: 5px; border-style: solid; border-color: #1F2937 transparent transparent transparent; }
.stepper-icon-item:hover .stepper-tooltip { opacity: 1; visibility: visible; bottom: 150%; }

.tiny-toggle-wrapper { display: inline-flex; cursor: pointer; align-items: center; justify-content: center; margin-left: auto; }
.tiny-switch { width: 44px; height: 24px; background: #E5E7EB; border-radius: 12px; position: relative; transition: background 0.3s ease; box-shadow: inset 0 1px 3px rgba(0,0,0,0.1); }
.tiny-switch.active { background: #FBB603; }
.tiny-knob { width: 20px; height: 20px; background: white; border-radius: 50%; position: absolute; top: 2px; left: 2px; transition: transform 0.3s cubic-bezier(0.4, 0.0, 0.2, 1); box-shadow: 0 1px 3px rgba(0,0,0,0.15); display: flex; align-items: center; justify-content: center; color: #6B7280; }
.tiny-switch.active .tiny-knob { transform: translateX(20px); color: #D97706; }

/* Buttons */
.btn-primary { background: #FBB603; color: white; border: none; padding: 0.8rem 1.8rem; border-radius: 8px; font-weight: 600; font-size: 0.95rem; cursor: pointer; transition: all 0.2s ease; font-family: 'Poppins', sans-serif;}
.btn-primary:hover:not(:disabled) { background: #E5A000; box-shadow: 0 4px 10px rgba(251, 182, 3, 0.2);}
.btn-primary:disabled { opacity: 0.5; cursor: not-allowed; transform: none; box-shadow: none;}
.btn-secondary { background: white; color: #374151; border: 1px solid #D1D5DB; padding: 0.7rem 1.5rem; border-radius: 8px; cursor: pointer; font-weight: 500; font-size: 0.9rem; transition: all 0.2s ease; font-family: 'Poppins', sans-serif;}
.btn-secondary:hover:not(:disabled) { border-color: #9CA3AF; color: #111827; }
.btn-text { background: none; border: none; color: #6B7280; font-weight: 500; cursor: pointer; padding: 0.5rem; font-size: 0.95rem; transition: color 0.2s; font-family: 'Poppins', sans-serif;}
.btn-text:hover:not(:disabled) { color: #111827; }
.toggle-group { display: inline-flex; background: #F3F4F6; padding: 4px; border-radius: 8px; border: 1px solid #E5E7EB;}
.toggle-btn { border: none; background: none; padding: 0.5rem 1.2rem; font-size: 0.85rem; border-radius: 6px; cursor: pointer; color: #6B7280; font-weight: 600; transition: all 0.2s ease; font-family: 'Poppins', sans-serif;}
.toggle-btn.active { background: white; color: #111827; box-shadow: 0 1px 3px rgba(0,0,0,0.05); }

/* --- RIBBON HISTORY OVERLAY MODAL --- */
.ribbon-modal-overlay { background: rgba(17, 24, 39, 0.6); backdrop-filter: blur(4px); }
.ribbon-modal-card { background: white; border-radius: 16px; width: 95%; max-width: 1100px; padding: 2.5rem; position: relative; box-shadow: 0 10px 25px rgba(0,0,0,0.1); border: 1px solid #E5E7EB; }
.absolute-close { position: absolute; top: 1.5rem; right: 1.5rem; background: #F3F4F6; border: none; width: 32px; height: 32px; border-radius: 50%; font-size: 1.2rem; color: #6B7280; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: all 0.2s;}
.absolute-close:hover { background: #E5E7EB; color: #111827; }
.ribbon-header-area { display: flex; justify-content: space-between; align-items: flex-end; border-bottom: 1px solid #F3F4F6; padding-bottom: 1rem;}
.ribbon-legend { display: flex; gap: 1.5rem; align-items: center; }
.legend-dash { width: 30px; height: 6px; border-radius: 3px; }
.legend-dash.solid { background: #FBB603; }
.legend-dash.dashed { border-top: 3px dashed #9CA3AF; height: 0; background: transparent; }

.ribbon-container { position: relative; width: 100%; height: 420px; background: white; border-radius: 12px; border: 1px solid #E5E7EB; overflow: hidden; cursor: grab;}
.ribbon-container.is-panning { cursor: grabbing; }
.ribbon-container.is-panning .history-card { pointer-events: none; }
.ribbon-svg { position: absolute; top: 0; left: 0; width: 100%; height: 100%; pointer-events: none; }
.ribbon-nodes-layer { position: absolute; top: 0; left: 0; width: 100%; height: 100%; pointer-events: none; }
.history-node-wrapper { position: absolute; transform: translate(-50%, -50%); pointer-events: auto; transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275); z-index: 10;}
.history-node-wrapper.is-dimmed { opacity: 0.4; transform: translate(-50%, -50%) scale(0.9); z-index: 5;}
.history-node-wrapper.is-hovered { opacity: 1; transform: translate(-50%, -50%) scale(1.05); z-index: 20;}
.history-dot { position: relative; width: 20px; height: 20px; display: flex; align-items: center; justify-content: center; margin: 0 auto; cursor: pointer;}
.dot-inner { width: 12px; height: 12px; background: white; border: 3px solid #F59E0B; border-radius: 50%; z-index: 2; box-shadow: 0 2px 4px rgba(0,0,0,0.1); transition: all 0.3s;}
.dot-inner.current { border-color: #EF4444; }
.dot-halo { position: absolute; width: 32px; height: 32px; border-radius: 50%; background: rgba(251, 182, 3, 0.2); animation: pulseHalo 1.5s infinite; z-index: 1;}

@keyframes pulseHalo { 0% { transform: scale(0.8); opacity: 1; } 100% { transform: scale(1.5); opacity: 0; } }

.history-card { position: absolute; left: 50%; width: 240px; background: rgba(255,255,255,0.95); backdrop-filter: blur(8px); border: 1px solid #E5E7EB; border-radius: 12px; padding: 1.2rem; box-shadow: 0 4px 10px rgba(0,0,0,0.05); text-align: left; cursor: pointer; transition: all 0.3s ease;}
.history-card.up { bottom: 30px; transform: translateX(-50%); border-bottom: 4px solid #FBB603;}
.history-card.down { top: 30px; transform: translateX(-50%); border-top: 4px solid #FBB603;}
.history-node-wrapper.is-hovered .history-card { box-shadow: 0 8px 20px rgba(0,0,0,0.1); border-color: #FDE68A;}
.history-card h3 { margin: 0; font-size: 1rem; color: #111827; font-weight: 700;}
.history-card .org { font-size: 0.8rem; color: #6B7280; margin: 0.2rem 0 0 0;}
.current-badge { position: absolute; top: 1.2rem; right: 1.2rem; background: #FEF2F2; color: #EF4444; font-size: 0.6rem; font-weight: 800; padding: 0.2rem 0.5rem; border-radius: 4px; letter-spacing: 0.05em;}
.skills-row { display: flex; flex-wrap: wrap; gap: 0.4rem; }
.mini-skill { font-size: 0.65rem; background: #F3F4F6; padding: 0.2rem 0.5rem; border-radius: 4px; color: #4B5563; font-weight: 600;}
.card-details-reveal { max-height: 0; opacity: 0; overflow: hidden; transition: all 0.3s ease; }
.history-node-wrapper.is-hovered .card-details-reveal { max-height: 200px; opacity: 1; }
.history-card .desc { font-size: 0.75rem; color: #4B5563; line-height: 1.4;}
.footer-meta { display: flex; justify-content: space-between; align-items: center; border-top: 1px dashed #E5E7EB; padding-top: 0.6rem;}
.footer-meta span { font-size: 0.65rem; font-weight: 700; color: #9CA3AF; letter-spacing: 0.05em;}
.inspect-btn { color: #F59E0B !important; cursor: pointer; }

.scrub-hint { position: absolute; bottom: 1rem; left: 0; right: 0; text-align: center; font-size: 0.8rem; font-style: italic; color: #9CA3AF; pointer-events: none;}
.ribbon-pan-zoom-layer { width: 100%; height: 100%; transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1); transform-origin: center center; }
.ribbon-toolbar { position: absolute; right: 1.5rem; top: 50%; transform: translateY(-50%); display: flex; flex-direction: column; gap: 0.5rem; opacity: 0; transition: all 0.3s ease; z-index: 50; }
.ribbon-container:hover .ribbon-toolbar { opacity: 1; }
.ribbon-toolbar button { width: 32px; height: 32px; border-radius: 6px; background: rgba(255, 255, 255, 0.9); border: 1px solid #E5E7EB; color: #4B5563; font-size: 1rem; cursor: pointer; box-shadow: 0 2px 4px rgba(0,0,0,0.05); display: flex; align-items: center; justify-content: center; transition: all 0.2s; }
.ribbon-toolbar button:hover { background: #FFFBEB; color: #D97706; border-color: #FCD34D; }

/* UTILS */
@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
@keyframes zoomIn { 0% { opacity: 0; transform: scale(0.9); } 100% { opacity: 1; transform: scale(1); } }
.fade-enter-active, .fade-leave-active { transition: opacity 0.2s ease; }
.fade-enter, .fade-leave-to { opacity: 0; }
.slide-down-enter-active, .slide-down-leave-active { transition: all 0.2s ease; max-height: 150px; opacity: 1; overflow: hidden;}
.slide-down-enter, .slide-down-leave-to { max-height: 0; opacity: 0; padding-top: 0; padding-bottom: 0; border-width: 0;}
.zoom-enter-active, .zoom-leave-active { transition: all 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275); }
.zoom-enter, .zoom-leave-to { opacity: 0; transform: scale(0.95) translateY(10px); }

.mt-2 { margin-top: 0.5rem; } .mt-3 { margin-top: 1rem; } .mt-4 { margin-top: 1.5rem; } .mt-5 { margin-top: 2rem; } .mt-6 { margin-top: 2.5rem; } .mt-8 { margin-top: 4rem; }
.mb-2 { margin-bottom: 0.5rem; } .mb-4 { margin-bottom: 1.5rem; } .mb-6 { margin-bottom: 2.5rem; } .mb-8 { margin-bottom: 3.5rem; }

/* History modal (small popup) */
.custom-error-modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0, 0, 0, 0.5); backdrop-filter: blur(4px); display: flex; align-items: center; justify-content: center; z-index: 999999; }
.custom-error-modal { background: white; padding: 2.5rem; border-radius: 16px; max-width: 400px; width: 90%; text-align: center; box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1); border-top: 4px solid #EF4444; animation: zoomIn 0.2s ease-out forwards; }
.custom-error-modal .modal-icon { font-size: 3rem; margin-bottom: 0.5rem; line-height: 1; }
.loader-spinner { width: 40px; height: 40px; border: 3px solid #F3F4F6; border-top: 3px solid #FBB603; border-radius: 50%; animation: spin 1s linear infinite; margin: 0 auto 1rem auto; }
.btn-spinner { display: inline-block; width: 16px; height: 16px; border: 2px solid rgba(255,255,255,0.3); border-top: 2px solid white; border-radius: 50%; animation: spin 1s linear infinite; vertical-align: middle; margin-right: 6px;}

.history-modal { background: white; padding: 2rem; border-radius: 16px; max-width: 450px; width: 90%; box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1); border-top: 4px solid #FBB603; animation: zoomIn 0.2s ease-out forwards; text-align: left;}
.history-modal .modal-header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 1px solid #F3F4F6; padding-bottom: 1rem;}
.history-modal h2 { font-size: 1.4rem; font-weight: 700; color: #111827; margin: 0.2rem 0 0 0;}
.history-modal .org-name { font-size: 0.9rem; color: #4B5563; font-weight: 500; margin: 0;}
.history-desc { font-size: 0.85rem; color: #374151; line-height: 1.5;}
.project-list { margin: 0; padding-left: 1.2rem; color: #4B5563; font-size: 0.85rem; line-height: 1.6;}
.project-list li { margin-bottom: 0.2rem;}

.time-readiness-view {
  width: 100%;
  display: flex;
  flex-direction: column;
}

/* TOP BANNER */
.trajectory-header-labels {
  display: flex;
  justify-content: space-between;
  padding: 0 1rem;
}
.text-purple-700 { color: #6D28D9; }
.tracking-widest { letter-spacing: 0.1em; }

.trajectory-banner {
  background: linear-gradient(90deg, #FCD34D 0%, #F97316 100%);
  border-radius: 12px;
  position: relative;
  box-shadow: 0 10px 20px -5px rgba(249, 115, 22, 0.3);
  padding: 1.5rem 0 2.5rem 0;
  overflow-x: auto; /* Enable clean horizontal scrolling if nodes overflow */
  overflow-y: hidden;
}

.trajectory-banner::-webkit-scrollbar {
  height: 6px;
}
.trajectory-banner::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.05);
  border-radius: 10px;
}
.trajectory-banner::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.3);
  border-radius: 10px;
}

.banner-title-text {
  text-align: center;
  color: white;
  font-weight: 800;
  letter-spacing: 0.3em;
  font-size: 0.9rem;
  margin-bottom: 2rem;
  text-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.trajectory-graph-area {
  position: relative;
  height: 250px;
  min-width: 1280px; /* Forces optimal node horizontal spacing */
  width: 100%;
}

.today-label {
  position: absolute;
  left: 2rem;
  top: -1.5rem;
  font-weight: 700;
  font-size: 0.75rem;
  color: rgba(0,0,0,0.6);
  letter-spacing: 0.1em;
}

.trajectory-lines-svg {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  width: 100%; height: 100%;
  pointer-events: none;
}

/* Magic property keeps strokes perfectly thin regardless of viewBox scaling */
.trajectory-path {
  stroke: #312e2e; /* Rich black */
  stroke-width: 2px;
  stroke-linecap: round;
  opacity: 0.5; /* Clean blend with the orange background */
  vector-effect: non-scaling-stroke;
  transition: all 0.3s ease;
}

.scatter-node-wrapper:hover ~ .trajectory-lines-svg .trajectory-path {
  opacity: 0.3; /* Soften non-focused paths */
}


.graph-nodes-layer {
  position: absolute;
  top: 0; left: 0; width: 100%; height: 100%;
  pointer-events: none;
}

.node-root-you {
  position: absolute;
  left: 6%; 
  top: 50%;
  transform: translate(-50%, -50%); /* Perfectly centers on the coordinate */
  width: 45px; height: 45px;
  background: white;
  border: 2px solid black;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  font-size: 0.75rem;
  color: black;
  z-index: 10;
  pointer-events: auto;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.scatter-nodes { position: relative; width: 100%; height: 100%; }

/* Wrapper ensures node and text stay aligned together */
.scatter-node-wrapper {
  position: absolute;
  /* Transform pulls it back 50% horizontally, and -20px vertically (exactly half the 40px circle) 
     This ensures the SVG line hits the dead-center of the circle perfectly. */
  transform: translate(-50%, -20px); 
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  pointer-events: auto;
  cursor: pointer;
  z-index: 20;
}

.node-time-circle {
  width: 40px;
  height: 40px;
  background: white;
  border: 2px solid #111827; /* Matching solid dark border */
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.75rem;
  color: #111827;
  /* 6px solid-like white halo masks the background SVG line to create a clearance gap */
  box-shadow: 0 0 0 6px rgba(255, 255, 255, 0.35), 0 4px 10px rgba(0, 0, 0, 0.15);
  transition: transform 0.2s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.2s;
}
/* Hover Interactivity */
.scatter-node-wrapper:hover .node-time-circle {
  transform: scale(1.15);
  box-shadow: 0 0 0 8px rgba(255, 255, 255, 0.4), 0 6px 15px rgba(0, 0, 0, 0.2);
}

.node-text-label {
  text-align: center;
  width: 140px; /* Fixed width prevents text wrapping weirdness */
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.node-text-label strong {
  display: block;
  font-size: 0.75rem;
  color: rgba(255,255,255,0.9);
  text-transform: uppercase;
  text-shadow: 0 1px 2px rgba(0,0,0,0.2);
  line-height: 1.2;
  transition: color 0.2s;
}

.node-text-label span {
  font-size: 0.65rem;
  color: rgba(255,255,255,0.7);
  line-height: 1.2;
}

.scatter-node-wrapper:hover .node-text-label strong {
  color: white;
}


/* BOTTOM COLUMNS */
.time-action-columns {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
  width: 100%;
}

.action-col {
  display: flex;
  flex-direction: column;
}

.col-header-area {
  text-align: center;
  margin-bottom: 1rem;
}

/* The decorative purple bracket */
.purple-bracket {
  position: relative;
  color: #8B5CF6;
  font-size: 0.8rem;
  font-style: italic;
  padding-top: 0.5rem;
  margin-bottom: 0.5rem;
}
.purple-bracket::before {
  content: '';
  position: absolute;
  top: 0; left: 15%; right: 15%;
  height: 6px;
  border-top: 1px solid #C4B5FD;
  border-left: 1px solid #C4B5FD;
  border-right: 1px solid #C4B5FD;
  border-radius: 4px 4px 0 0;
}

.col-main-title {
  font-size: 1.4rem;
  font-weight: 800;
  margin: 0 0 0.2rem 0;
}
.text-green-500 { color: #10B981; }
.text-yellow-500 { color: #F59E0B; }
.text-orange-500 { color: #F97316; }

.col-sub-title {
  font-size: 0.7rem;
  font-weight: 600;
  color: #9CA3AF;
  letter-spacing: 0.05em;
}

/* Box Containers */
.action-box {
  flex: 1;
  border-radius: 12px;
  padding: 1.5rem;
  border: 1px solid;
}
.bg-green-light { background-color: #ECFDF5; }
.border-green-light { border-color: #A7F3D0; }

.bg-yellow-light { background-color: #FFFBEB; }
.border-yellow-light { border-color: #FDE68A; }

.bg-orange-light { background-color: #FFF7ED; }
.border-orange-light { border-color: #FFEDD5; }

.box-intro-text {
  font-size: 0.85rem;
  color: #4B5563;
  margin-bottom: 1.5rem;
  line-height: 1.5;
}

/* Interactive White Cards inside columns */
.role-cards-wrapper {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}
.role-cards-wrapper.empty {
  font-style: italic;
  color: #9CA3AF;
  font-size: 0.85rem;
}

.action-role-card {
  background: white;
  border-radius: 8px;
  padding: 1rem 1.25rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(0,0,0,0.03);
  transition: all 0.2s ease;
  border: 1px solid transparent;
}
.action-role-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 10px rgba(0,0,0,0.05);
  border-color: #E5E7EB;
}

.card-role-title {
  font-weight: 700;
  font-size: 0.9rem;
  color: #111827;
}
.card-role-time {
  font-weight: 600;
  font-size: 0.8rem;
  color: #9CA3AF;
}

.left-aligned-card {
  text-align: left !important;
  margin: 0 auto;
  max-width: 650px;
  padding: 3.5rem 3rem;
}

.left-aligned-card .step-icon-sm {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: #FFFBEB;
  font-size: 1.6rem;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
  box-shadow: 0 4px 15px rgba(251, 182, 3, 0.15);
  border: 1px solid #FDE68A;
}

.left-aligned-card .huge-title { 
  margin-bottom: 0.5rem; 
  font-size: 2rem; 
}

.left-aligned-card .subtitle { 
  margin-left: 0; 
  max-width: 100%; 
  color: #6B7280 !important; 
}

.left-aligned-card .description-text { 
  font-size: 0.85rem; 
  color: #9CA3AF !important; 
  line-height: 1.6; 
}

.input-group { 
  width: 100%; 
  display: flex; 
  flex-direction: column; 
}

/* Pill-shaped Select Dropdown */
.pill-select {
  width: 100%;
  padding: 1.1rem 1.5rem;
  font-size: 1rem;
  border: 2px solid #E5E7EB;
  border-radius: 999px;
  background: white;
  outline: none;
  transition: all 0.2s;
  cursor: pointer;
  color: #111827;
  font-family: 'Poppins', sans-serif;
  font-weight: 500;
  appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%239CA3AF' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 1.5rem center;
  background-size: 1.2em;
}
.pill-select:invalid, 
.pill-select option[value=""] {
  color: #9CA3AF !important; /* Muted gray for the placeholder text */
}

.pill-select:focus, 
.pill-select.active-border {
  border-color: #FBB603;
  box-shadow: 0 0 0 4px rgba(251, 182, 3, 0.15);
}

/* Pill-shaped Dashed Input */
.pill-input-dashed {
  width: 100%;
  padding: 1.1rem 1.5rem;
  font-size: 0.95rem;
  border: 2px dashed #D1D5DB;
  border-radius: 999px;
  background: white;
  outline: none;
  transition: all 0.2s;
  color: #111827;
  font-family: 'Poppins', sans-serif;
}

.pill-input-dashed:focus {
  border-color: #9CA3AF;
  border-style: solid;
}

.left-aligned-card.wide-card {
  max-width: 850px; /* Wider card to fit the skill pills nicely */
}

.pills-container.left-align-pills {
  display: flex !important;
  flex-wrap: wrap !important;
  justify-content: flex-start !important;
  gap: 0.5rem 0.5rem !important; /* 1.25rem vertical space, 1rem horizontal space */
  margin-top: 1rem !important;
}

/* Let's also ensure the pills themselves aren't collapsing */
.skill-pill-outline {
  display: inline-flex;
  align-items: center;
  padding: 0.75rem 1.4rem;
  background: white;
  border: 1px solid #E5E7EB;
  border-radius: 999px;
  font-size: 0.95rem;
  color: #4B5563;
  cursor: pointer;
  transition: all 0.2s ease;
  user-select: none;
  font-weight: 500;
  white-space: nowrap; /* Prevents text inside the pill from wrapping weirdly */
}

.skill-pill-outline:hover {
  border-color: #D1D5DB;
  color: #111827;
}

/* Matches the active state in Pic 3 (White background, orange border/text) */
.skill-pill-outline.active {
  background: white;
  border-color: #FBB603;
  color: #D97706; /* Darker orange for text readability */
  box-shadow: 0 0 0 1px #FBB603; /* Creates a 2px border effect without changing element size */
  font-weight: 600;
}

.centered-pane {
  display: flex;
  flex-direction: column;
  align-items: center; /* This centers the white box horizontally on the page */
  width: 100%;
  margin-top: 3rem; /* Spacing below the stepper */
}

.form-card.left-aligned-card {
  background: #FFFFFF;
  border: 1px solid #F3F4F6;
  border-radius: 24px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.03);
  padding: 3.5rem 4rem;
  width: 100%;
  max-width: 850px; /* EXACT SAME SIZE for all 4 steps */
  margin: 0 auto; /* Ensures the box stays centered */
  
  /* THIS FORCES INNER CONTENT TO THE LEFT */
  display: flex !important;
  flex-direction: column !important;
  align-items: flex-start !important; 
  text-align: left !important;
}

/* --- INNER CONTENT ALIGNMENT --- */
.left-aligned-card .step-icon-sm {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #FFFBEB;
  font-size: 1.8rem;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 0 1.5rem 0 !important; /* Overrides any margin: auto centering */
  box-shadow: 0 4px 15px rgba(251, 182, 3, 0.15);
  border: 1px solid #FDE68A;
}

.left-aligned-card .huge-title,
.left-aligned-card .subtitle,
.left-aligned-card .description-text {
  text-align: left !important;
  margin-left: 0 !important;
  width: 100%;
}

.left-aligned-card .input-group,
.left-aligned-card .pill-input-dashed {
  width: 100%; /* Makes inputs stretch across the whole box */
}

/* --- PILLS CONTAINER --- */
.pills-container.left-align-pills {
  display: flex !important;
  flex-wrap: wrap !important;
  justify-content: flex-start !important; /* aligns pills to the left */
  gap: 1.25rem 1rem !important; /* 1.25rem vertical space, 1rem horizontal */
  width: 100% !important;
}

.clickable-tag {
  cursor: pointer;
  background: #F3F4F6;
  color: #4B5563 !important;
  font-weight: 600;
  transition: all 0.2s ease;
  border: 1px dashed #D1D5DB;
}
.clickable-tag:hover {
  background: #E5E7EB;
  color: #111827 !important;
  border-color: #9CA3AF;
  transform: translateY(-1px);
}
.upload-box.is-parsing {
  border: none !important;
  background: transparent !important;
  box-shadow: none !important;
  padding: 0 !important;
}

.radar-parsing-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 2rem 0;
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
}

.radar-title {
  font-size: 1.3rem; /* Larger */
  font-weight: 800;
  color: #111827;
  margin: 2rem 0 0.5rem 0;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.radar-status-text {
  font-size: 0.95rem; /* Larger */
  font-weight: 600;
  color: #F59E0B; 
  margin: 0;
  height: 24px; 
  animation: pulseText 1.5s infinite;
}

@keyframes pulseText { 0% { opacity: 0.6; } 50% { opacity: 1; } 100% { opacity: 0.6; } }

/* The Larger Document Container */
.radar-document-container {
  position: relative;
  width: 320px; /* Much larger */
  height: 400px; /* Much larger */
  background: white;
  border: 1px solid #E5E7EB;
  border-radius: 16px;
  padding: 2.5rem 2rem;
  box-shadow: 0 15px 35px rgba(0,0,0,0.08);
  /* Remove overflow:hidden so badges can stick out! */
}

/* Skeleton Resume Elements */
.sk-header { display: flex; gap: 1.5rem; margin-bottom: 2rem; }
.sk-avatar { width: 50px; height: 50px; border-radius: 50%; background: #F3F4F6; }
.sk-lines { flex: 1; display: flex; flex-direction: column; gap: 0.6rem; justify-content: center; }
.sk-line { height: 10px; background: #F3F4F6; border-radius: 5px; }
.w-90 { width: 90%; } .w-85 { width: 85%; } .w-80 { width: 80%; } 
.w-75 { width: 75%; } .w-60 { width: 60%; } .w-50 { width: 50%; } .w-40 { width: 40%; }
.mt-3 { margin-top: 1.5rem; }

/* The Sweeping Laser */
.scanner-laser-group {
  position: absolute;
  left: 0; right: 0; top: 0;
  height: 100px; /* Larger trail */
  transform: translateY(-100%);
  animation: sweepRadar 3.5s cubic-bezier(0.4, 0, 0.2, 1) infinite alternate;
  pointer-events: none;
  z-index: 10;
  border-radius: 16px;
  overflow: hidden;
}
.scanner-line {
  position: absolute; bottom: 0; width: 100%; height: 3px;
  background: #FBB603; box-shadow: 0 0 10px #FBB603, 0 0 20px #F59E0B;
}
.scanner-gradient {
  position: absolute; bottom: 3px; width: 100%; height: 100%;
  background: linear-gradient(to top, rgba(251, 182, 3, 0.2) 0%, rgba(251, 182, 3, 0) 100%);
}
@keyframes sweepRadar {
  0% { transform: translateY(-100%); }
  100% { transform: translateY(420px); }
}

/* Magnifying Glass Roaming Animation */
.mag-glass-wrapper {
  position: absolute;
  z-index: 15;
  top: 20%; left: 20%;
  animation: roamGlass 8s ease-in-out infinite alternate;
  pointer-events: none;
}
.mag-glass {
  width: 80px; height: 80px;
  border-radius: 50%;
  border: 4px solid rgba(251, 182, 3, 0.8);
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(4px); /* Creates the magnifying distortion */
  box-shadow: inset 0 0 15px rgba(251, 182, 3, 0.3), 0 10px 20px rgba(0,0,0,0.1);
}
.mag-handle {
  position: absolute;
  bottom: -25px; right: -15px;
  width: 12px; height: 40px;
  background: #D1D5DB;
  border-radius: 6px;
  transform: rotate(-45deg);
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
}
@keyframes roamGlass {
  0% { transform: translate(0, 0) scale(1); }
  25% { transform: translate(120px, 40px) scale(1.1); }
  50% { transform: translate(40px, 180px) scale(0.9); }
  75% { transform: translate(-30px, 120px) scale(1.05); }
  100% { transform: translate(80px, 220px) scale(1); }
}

/* Floating Pop-up Badges (Bigger, sticking out of the sides) */
.float-badge {
  position: absolute;
  background: white;
  border: 1px solid #FDE68A;
  color: #111827;
  font-size: 0.8rem;
  font-weight: 700;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  box-shadow: 0 8px 20px rgba(251, 182, 3, 0.15);
  z-index: 20;
  opacity: 0;
  white-space: nowrap;
  animation: popInOut 6s infinite;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.badge-dot { width: 8px; height: 8px; background: #10B981; border-radius: 50%; box-shadow: 0 0 8px #10B981; }

/* Connectors pointing to the document */
.connector-line {
  position: absolute; top: 50%; height: 2px; background: #FDE68A; width: 30px; z-index: -1;
}
.left-line { right: -30px; } /* Points right into the document */
.right-line { left: -30px; } /* Points left into the document */

/* Stagger positions outside the box */
.badge-1 { top: 15%; left: -35%; animation-delay: 0.2s; }
.badge-2 { top: 40%; right: -35%; animation-delay: 2s; }
.badge-3 { top: 65%; left: -35%; animation-delay: 3.8s; }
.badge-4 { top: 85%; right: -35%; animation-delay: 5.5s; }

@keyframes popInOut {
  0% { opacity: 0; transform: scale(0.8); }
  10% { opacity: 1; transform: scale(1); }
  40% { opacity: 1; transform: scale(1); }
  50% { opacity: 0; transform: scale(0.8); }
  100% { opacity: 0; }
}

/* --- RIGHT SIDE SKELETON PREVIEW (STAGE 2) --- */
.skeleton-box {
  background: white;
  border: 1px dashed #E5E7EB;
  border-radius: 12px;
  padding: 2rem;
  min-height: 400px;
}
.sk-pulse-circle { width: 48px; height: 48px; border-radius: 50%; background: #F3F4F6; animation: pulseBg 1.5s infinite; }
.sk-pulse-line { background: #F3F4F6; border-radius: 6px; animation: pulseBg 1.5s infinite; height: 12px; }
.sk-pulse-tag { background: #F3F4F6; border-radius: 8px; animation: pulseBg 1.5s infinite; height: 32px; width: 100px; }
.sk-pulse-tag.w-s { width: 70px; }
.sk-pulse-tag.w-l { width: 140px; }
.w-24 { width: 80px; } .w-32 { width: 120px; } .w-full { width: 100%; } .h-8 { height: 24px !important; }
@keyframes pulseBg { 0% { opacity: 0.5; } 50% { opacity: 1; } 100% { opacity: 0.5; } }
.upload-box {
  /* Ensure the main upload box removes the dashed border when successful */
  transition: all 0.3s ease;
}
.upload-box:has(.success-state-wrapper) {
  border: none !important;
  background: transparent !important;
}

.success-state-wrapper {
  width: 100%;
  height: 100%;
  display: flex;
}

.resume-preview-card {
  width: 100%;
  height: 100%; /* Matches the exact height of the right box */
  background: white;
  border-radius: 12px;
  border: 1px solid #E5E7EB;
  box-shadow: 0 10px 30px rgba(0,0,0,0.04);
  position: relative;
  overflow: hidden;
}

/* Skeleton Building Blocks */
.sk-line { background: #F3F4F6; border-radius: 4px; height: 8px; }
.sk-accent { background: #FDE68A; border-radius: 4px; height: 8px; }
.sk-block { background: #F9FAFB; border-radius: 6px; }
.sk-circle { width: 60px; height: 60px; background: #E5E7EB; border-radius: 50%; }
.sk-circle-sm { width: 40px; height: 40px; background: #E5E7EB; border-radius: 50%; }
.sk-section-title { width: 30%; height: 10px; background: #E5E7EB; border-radius: 4px; }

/* Width Utilities */
.w-20 { width: 20%; } .w-30 { width: 30%; } .w-40 { width: 40%; }
.w-50 { width: 50%; } .w-60 { width: 60%; } .w-75 { width: 75%; }
.w-80 { width: 80%; } .w-85 { width: 85%; } .w-90 { width: 90%; }
.w-full { width: 100%; }
.h-5 { height: 12px; } .h-6 { height: 16px; } .h-16 { height: 48px; }

/* Layout 1: Split */
.layout-split { display: flex; height: 100%; }
.sk-sidebar { width: 35%; background: #F9FAFB; padding: 3rem 1.5rem; border-right: 1px solid #F3F4F6; }
.sk-main { width: 65%; padding: 3rem 2rem; }

/* Layout 2: Centered */
.layout-centered { padding: 3rem 2rem; }
.sk-header-centered { display: flex; flex-direction: column; align-items: center; text-align: center; border-bottom: 2px solid #F3F4F6; padding-bottom: 1.5rem; }
.sk-body-grid { display: flex; gap: 1.5rem; }
.sk-col { flex: 1; }
.sk-exec-body { margin-top: 2.5rem; }

/* Layout 3: Executive */
.layout-executive { padding: 1rem 2.5rem 2.5rem 2.5rem; }
.sk-top-bar { position: absolute; top: 0; left: 0; right: 0; height: 12px; background: #FBB603; }
.sk-exec-header { padding-top: 1rem; border-bottom: 1px solid #E5E7EB; padding-bottom: 1.5rem; }
.sk-flex-row { display: flex; gap: 1rem; align-items: flex-start; }

/* Floating Success Panel (Overlays the bottom of the skeleton) */
.success-floating-panel {
  position: absolute;
  bottom: 1.5rem; 
  left: 1.5rem;
  right: 1.5rem;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(12px);
  border: 1px solid #E5E7EB;
  border-radius: 12px;
  padding: 1rem; 
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.5rem; /* Ensure space between text and button */
  box-shadow: 0 8px 20px rgba(0,0,0,0.08);
}

.panel-left {
  display: flex;
  align-items: center;
  gap: 0.8rem;
  flex: 1; /* Take up remaining space */
  min-width: 0; /* CRITICAL: Allows text inside flexbox to truncate properly */
}

.success-icon-sm {
  width: 28px;
  height: 28px;
  flex-shrink: 0; /* CRITICAL: Prevents it from becoming an oval! */
  background: #10B981;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  font-size: 0.9rem;
}

.panel-text h4 {
  margin: 0;
  font-size: 0.85rem;
  font-weight: 700;
  color: #111827;
}

.panel-text {
  min-width: 0; /* CRITICAL for ellipsis */
  display: flex;
  flex-direction: column;
}

.panel-text p {
  margin: 0;
  font-size: 0.75rem;
  color: #6B7280;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%; /* Let it fill available space without pushing button */
  max-width: none; /* Remove previous hardcoded max-width */
}

.change-btn {
  flex-shrink: 0; 
  white-space: nowrap; 
  font-size: 0.8rem !important;
  color: #F59E0B !important;
  font-weight: 700 !important;
  padding: 0.5rem 0.8rem !important;
  background: #FFFBEB !important;
  border: 1px solid #FDE68A !important;
  border-radius: 8px;
  transition: all 0.2s ease;
  user-select: none;
}
.h-10 { height: 32px; }
.change-btn:hover {
  background: #FEF3C7 !important;
  border-color: #FCD34D !important;
  transform: translateY(-1px);
}
.anim-pop-in {
  opacity: 0;
  transform: scale(0.8);
  animation: popInAnim 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
}

.anim-slide-right {
  opacity: 0;
  transform: translateX(-15px);
  animation: slideRightAnim 0.4s ease-out forwards;
}

.anim-slide-up {
  opacity: 0;
  transform: translateY(10px);
  animation: slideUpAnim 0.4s ease-out forwards;
}

.anim-fill-bar {
  width: 0 !important;
  animation: fillBarAnim 1s ease-out forwards;
}

@keyframes popInAnim {
  to { opacity: 1; transform: scale(1); }
}
@keyframes slideRightAnim {
  to { opacity: 1; transform: translateX(0); }
}
@keyframes slideUpAnim {
  to { opacity: 1; transform: translateY(0); }
}
@keyframes fillBarAnim {
  /* We use the width set in the inline style, so we just animate width from 0 */
  from { width: 0; }
}
.velocity-tooltip {
  top: 135% !important;     /* Drops it below the badge */
  bottom: auto !important;  /* Overrides global bottom push */
  left: auto !important;    /* Overrides global left centering */
  right: 0 !important;      /* Aligns it to the right edge of the badge */
  transform: none !important; /* Stops it from shifting left */
  width: 280px !important;  /* Gives it enough breathing room */
  white-space: normal !important; /* Allows text to wrap to next line */
  text-align: left !important;
  padding: 12px 14px !important;
  line-height: 1.5 !important;
  font-weight: 500 !important;
  border-radius: 8px !important;
  z-index: 99999 !important; /* Ensures it stays above all other cards */
  box-shadow: 0 10px 25px rgba(0,0,0,0.15);
}

/* Moves the little pointer arrow from the bottom to the TOP of the tooltip */
.velocity-tooltip::after {
  content: '';
  position: absolute;
  top: -10px;           /* Puts arrow above the box */
  bottom: auto;
  right: 20px;          /* Aligns arrow near the right side */
  left: auto;
  margin-left: 0;
  border-width: 0 6px 10px 6px; /* Arrow pointing UP */
  border-style: solid;
  border-color: transparent transparent #1F2937 transparent;
}

.clickable-resume {
  cursor: pointer;
  transition: all 0.3s ease !important;
}

.clickable-resume:hover {
  transform: translateY(-4px);
  box-shadow: 0 15px 35px rgba(251, 182, 3, 0.15) !important;
  border-color: #FBB603 !important;
}

.clickable-resume:hover .change-btn {
  background: #FEF3C7 !important;
  border-color: #FCD34D !important;
  transform: translateY(-1px);
}

.clickable-resume:hover .sk-top-bar {
  /* Makes the top yellow bar glow when hovered */
  box-shadow: 0 0 10px rgba(251, 182, 3, 0.5); 
}
.resume-pdf-modal {
  background: white;
  padding: 2rem;
  border-radius: 16px;
  width: 90%;
  max-width: 850px; /* Big enough to comfortably read a PDF */
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
  border-top: 4px solid #FBB603;
  animation: zoomIn 0.2s ease-out forwards;
  text-align: left;
}

.pdf-container iframe {
  background: white; /* Ensures transparent PDFs still look like paper */
}
.scatter-node-wrapper {
  position: absolute;
  transform: translate(-50%, -50%); /* Centers the coordinate system precisely */
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  pointer-events: auto;
  cursor: pointer;
  z-index: 20;
}

.node-text-label {
  position: absolute;
  width: 150px;
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 2px;
  pointer-events: none;
}

.label-above {
  bottom: 46px; /* Offset spacing above the node */
}

.label-below {
  top: 46px; /* Offset spacing below the node */
}

</style>